/**
 * Business – Lagerhaus
 * /business/lagerhaus
 *
 * Bestandsansicht aller Lager und Verstecke.
 * Enthält: Slot-Übersicht, Inventar-Details, Markt-Lieferungen, Warentransporte.
 */

import { api, ApiError } from '../../services/api.js';
import { formatWarehouseName, formatItemKey } from '../../utils/formatters.js';
import { formatHideoutName } from '../../utils/hideout-format.js';
import { renderPropertyOptions, formatPropertyDisplayName, type PropertySelectItem } from '../../utils/property-select.js';
import { renderItemBadges } from '../../utils/item-badges.js';
import { showToast } from '../../utils/toast.js';
import { refreshBalance } from '../../utils/refreshBalance.js';
import {
  isPackItem,
  packCount,
  packsToUnits,
  packBadgeCssVar,
  getPackUnitsPerPack,
} from '../../utils/pack-items.utils.js';
import { computeTransportPhaseUi } from '../../utils/transport-phases.js';
import { renderWhenIdle, cancelPendingRender } from '../../utils/background-render.js';

const LAGERHAUS_RENDER_KEY = 'lagerhaus';
import { tryOfferEmergencyGarage } from '../../components/EmergencyGarageOfferModal.js';

// ─── Typen ────────────────────────────────────────────────────────────────────

interface InventorySummary { materials: number; laborInputs: number; laborOutputs: number; fenceGoods: number; vehicles: number; }
interface InventoryItem    { itemType: string; itemKey: string; quantity: number; slotCost: number; }

// ─── Konsumierbare (NT-CONSUMABLE-01) ──────────────────────────────────────────

interface ConsumableInventoryEntry {
  itemType:            string;
  nameDE:              string;
  descriptionDE:       string;
  effectDescriptionDE: string;
  category:        'ZEIT' | 'WIRTSCHAFT' | 'SCHUTZ' | 'RUF' | 'SELTEN';
  rarity:          'COMMON' | 'UNCOMMON' | 'RARE' | 'EPIC';
  maxStack:        number;
  dailyCap:        number;
  requiresTarget:  boolean;
  quantity:        number;
  dailyUsageCount: number;
}

interface GrowingPlantOption { id: string; strainKey: string; }
interface JobCooldownOption { jobId: string; jobName: string; }

interface OwnedProperty {
  id:               string;
  type?:            string;
  subType:          string;
  label:            string;
  customName:       string | null;
  nickname:         string | null;
  slots:            number;
  currentLoad:      number;
  raidRiskPerFL:    number;
  isLabor?:         boolean;
  isActive:         boolean;
  inventory:        InventorySummary;
  inventoryItems:   InventoryItem[];

  // Nur bei Lagern gesetzt (NT-STORAGE-UPGRADE-01) — Verstecke sind nicht ausbaubar.
  storageUpgradeLevel?:       number;
  maxStorageLevel?:           number;
  storageUpgradeCompletesAt?: string | null;
  nextUpgradeCostCents?:      string | null;
  nextUpgradeMinutes?:        number | null;
  hasVehicleSlot?:            boolean;
  assignedVehicleId?:         string | null;
  assignedVehicleKey?:        string | null;

  // Nur bei Lagern gesetzt (NT-PFAND-LAGER-LOOPB-01) — null = generisch nutzbar.
  dedicatedItemKey?: string | null;
}

interface GarageVehicle {
  vehicleKey:            string;
  quantity:              number;
  isSlotFree:            boolean;
  dealerAssignedCount:   number;
  propertyAssignedCount: number;
  busyCount:             number;
}

interface GarageInfo {
  totalSlots:    number;
  occupiedSlots: number;
  freeSlots:     number;
  vehicles:      GarageVehicle[];
  baseVehicles:  GarageVehicle[];
}

interface PropertiesResponse {
  warehouses:          OwnedProperty[];
  hideouts:            OwnedProperty[];
  totalWarehouseSlots: number;
  totalHideoutSlots:   number;
  totalUsedSlots:      number;
  garage:              GarageInfo;
}

interface VehicleSlotView {
  slotIndex: number;
  itemKey:   string | null;
  quantity:  number | null;
}

interface VehicleInventoryData {
  vehicleId:     string;
  vehicleKey:    string;
  label:         string;
  capacity:      number;
  occupiedSlots: number;
  freeSlots:     number;
  slots:         VehicleSlotView[];
}

interface VehicleListItem {
  id:          string;
  vehicleKey:  string;
  label:       string;
  capacity:    number;
  speedFactor: number;
  role:        string;
  /** NT-HOTFIX-01 (H1): vom Backend — keine Rolle, nicht unterwegs, kein Lager-Fahrzeugslot. */
  isAvailable: boolean;
  /** NT-VIERTEL-FIX-01 (V1): Stellplatz-Pflicht / aktueller Heimatplatz / umstellbar. */
  requiresStellplatz: boolean;
  stellplatzPropertyId: string | null;
  canMoveStellplatz: boolean;
  /** Erlös bei Verkauf heute (Cent als String). */
  sellPriceCents: string;
}

interface PendingOrder {
  id:               string;
  itemKey:          string;
  itemLabel:        string;
  quantity:         number;
  targetPropertyId: string;
  deliveredAt:      string;
  remainingMs:      number;
}

interface TransportInfo {
  id:             string;
  vehicleKey:     string;
  fromPropertyId: string;
  fromLabel:      string;
  toPropertyId:   string;
  toLabel:        string;
  itemKey:        string;
  itemType:       string;
  quantity:       number;
  bundleItems?:   { itemKey: string; itemType: string; quantity: number }[];
  startedAt:      string;
  loadingEndsAt:     string | null;
  unloadingStartsAt: string | null;
  arrivesAt:      string;
  remainingMs:    number;
  status:         string;
}

interface TransportStatusResponse {
  active:      TransportInfo[];
  recent:      TransportInfo[];
  maxParallel: number;
  canStart:    boolean;
}

// Scheine-Skip fürs Warten (Säule 3 QoL-Zeit-Skips, nighttrade_monetarisierung_v3.md) —
// Kosten sind serverseitig in TRANSPORT_SKIP_COST_SCHEINE fixiert, hier nur Anzeige.
const TRANSPORT_SKIP_COST_SCHEINE_DISPLAY = 8;

// ─── State ────────────────────────────────────────────────────────────────────

let propsData:         PropertiesResponse | null    = null;
let pendingOrders:     PendingOrder[]                = [];
let transportData:     TransportStatusResponse | null = null;
let vehicleInventories: VehicleInventoryData[]        = [];
let vehicleList:        VehicleListItem[]              = [];
let refreshTimer:      ReturnType<typeof setInterval> | null = null;
let countdownTimer:    ReturnType<typeof setInterval> | null = null;
const expandedCards              = new Set<string>();
const expandedTransportDetails              = new Set<string>();
let activeTab:                'lager' | 'garage' | 'konsumierbare' = 'lager';
let consumables:      ConsumableInventoryEntry[]  = [];
let growingPlants:    GrowingPlantOption[]         = [];
let jobsInCooldown:   JobCooldownOption[]          = [];
const expandedVehicleCards              = new Set<string>();

// Transfer-Modal State (schnelles Einzelitem-VERSCHIEBEN)
interface TransferModal {
  fromPropertyId: string;
  itemKey:        string;
  itemType:       string;
  availableQty:   number;
  qty:            number;
  toPropertyId:   string;
  vehicleKey:     string;
}
let transferModal: TransferModal | null = null;

// ─── Item-Kategorien für „Lager Versenden" (feste Sektions-Reihenfolge, NT-UI-DEALER-LIEFERN-01 #6) ──
// Sprechende Sammel-Kategorien statt 1:1 `itemType` (InventoryItemType hat 13 Werte, u.a. drei
// getrennte Tabak-Stufen) — mit Chris abgestimmt: spielerfreundliche Gruppierung statt DB-Jargon.
const LAGER_KATEGORIE_ORDER: readonly string[] = [
  'GRAS', 'TABAK', 'LABOR_OUTPUT', 'ROLLING_OUTPUT', 'ZUTATEN', 'AUSRUESTUNG', 'HEHLERWARE', 'SCHMUGGELWARE', 'MATERIAL',
];

const LAGER_KATEGORIE_LABEL: Record<string, string> = {
  GRAS:           'GRAS',
  TABAK:          'TABAK',
  LABOR_OUTPUT:   'LABORPRODUKTE',
  ROLLING_OUTPUT: 'ROLLPRODUKTE',
  ZUTATEN:        'ZUTATEN',
  AUSRUESTUNG:    'AUSRÜSTUNG',
  HEHLERWARE:     'HEHLERWARE',
  SCHMUGGELWARE:  'SCHMUGGELWARE',
  MATERIAL:       'MATERIAL',
  SONSTIGES:      'SONSTIGES',
};

// itemType (InventoryItemType) → Sammel-Kategorie. VEHICLE wird vor der Gruppierung bereits
// gefiltert (sendableItems) und taucht hier daher nicht auf.
const ITEM_TYPE_TO_KATEGORIE: Record<string, string> = {
  GARDEN_OUTPUT:  'GRAS',
  TOBACCO_WET:    'TABAK',
  TOBACCO_DRIED:  'TABAK',
  TOBACCO_CUT:    'TABAK',
  LABOR_OUTPUT:   'LABOR_OUTPUT',
  ROLLING_OUTPUT: 'ROLLING_OUTPUT',
  LABOR_INPUT:    'ZUTATEN',
  ROLLING_SUPPLY: 'ZUTATEN',
  GARDEN:         'AUSRUESTUNG',
  LAB_EQUIPMENT:  'AUSRUESTUNG',
  FENCE_GOODS:    'HEHLERWARE',
  SMUGGLED_GOODS: 'SCHMUGGELWARE',
  MATERIAL:       'MATERIAL',
};

function getLagerKategorie(itemType: string): string {
  return ITEM_TYPE_TO_KATEGORIE[itemType] ?? 'SONSTIGES';
}

// VERSENDEN-Modal State (Mehrfachauswahl pro Lager)
interface SendItem {
  itemKey:        string;
  itemType:       string;
  available:      number;   // Für Pack-Items (blaettchen/kapselhuelsen): Pack-Anzahl; sonst: rohe Einheiten
  sheetsAvailable?: number; // Nur für Pack-Items: rohe Einheiten-Anzahl (für packsToUnits-Konvertierung)
  selected:       boolean;
  qty:            number;   // Für Pack-Items: Pack-Anzahl; sonst: rohe Einheiten
}

interface SendModal {
  fromPropertyId: string;
  vehicleKey:     string;
  items:          SendItem[];
  toPropertyId:   string;
  errorMsg:       string | null;
}
let sendModal: SendModal | null = null;

// Dauer-Vorschau (NT-TRANSPORT-BELADUNG-01 Rev. 2) — reiner Anzeige-Text, wird per Debounce
// nachgeladen (Vehicle-/Item-/Mengenänderung) und in-place gepatcht, kein Full-Re-Render.
interface TransportPreview {
  loadingMinutes:   number;
  travelMinutes:    number;
  unloadingMinutes: number;
  totalMinutes:     number;
  steps:            number; // NT-VIERTEL-02
}
let sendDurationPreviewText = '';
let sendPreviewDebounceTimer: ReturnType<typeof setTimeout> | null = null;
let sendPreviewRequestSeq = 0;

interface UnloadGroupedItem {
  itemKey:     string;
  totalQty:    number;
  transferQty: number;
  slotCount:   number;
  slots:       { slotIndex: number; quantity: number }[];
  selected:    boolean;
}

interface UnloadModal {
  vehicleId:    string;
  vehicleLabel: string;
  capacity:     number;
  occupiedSlots: number;
  items:        UnloadGroupedItem[];
  toPropertyId: string;
  errorMsg:     string | null;
}
let unloadModal: UnloadModal | null = null;

interface SellableItem {
  itemKey:      string;
  label:        string;
  quantity:     number;
  priceType:    string;
  pricePerUnit: string; // BigInt als String
}

interface SellModal {
  propertyId:      string;
  propertyName:    string;
  items:           SellableItem[];
  selectedItemKey: string | null;
  qty:             number;
  errorMsg:        string | null;
}
let sellModal: SellModal | null = null;
/** Fahrzeug, dessen Verkauf gerade auf Bestätigung wartet (Inline-Bestätigung in der Garage). */
let vehicleSellConfirmId: string | null = null;

// AN HEHLER-Modal State (Mehrfachauswahl, analog VERSENDEN — Hehler-Kommission NT-HEHLER-KANAL-01)
// Feste Item-Liste (kein Shared-Import für reine Anzeige-Werte, etabliertes Pattern) — muss zur
// HEHLER-Kategorie in market-economy.definitions.ts passen, echte Prüfung läuft serverseitig.
const HEHLER_ITEM_KEYS = new Set([
  'smartphone_guenstig', 'smartphone_mittel', 'smartphone_teuer',
  'armbanduhr_einfach', 'armbanduhr_wertvoll',
  'laptop_alt', 'laptop_neu',
  'schmuck_kette', 'schmuck_ring',
  'bargeld_buendel',
]);
const BARGELD_BUENDEL_KEY = 'bargeld_buendel';
const HEHLER_SKIP_COST_SCHEINE_DISPLAY = 10;
const HEHLER_MAX_ACTIVE_ORDERS_DISPLAY = 3;

// Fahrzeugslot-Kosten (NT-STORAGE-UPGRADE-01) — serverseitig in VEHICLE_SLOT_COST_CENTS fixiert, hier nur Anzeige.
const VEHICLE_SLOT_COST_CENTS_DISPLAY = '250000';

interface HehlerModalItem { itemKey: string; itemType: string; available: number; selected: boolean; qty: number; }
interface HehlerModal {
  propertyId:   string;
  propertyName: string;
  items:        HehlerModalItem[];
  errorMsg:     string | null;
}
let hehlerModal: HehlerModal | null = null;

interface LaundryBusinessOption { id: string; type: string; status: string; }
interface WashModal {
  propertyId:         string;
  available:          number;
  qty:                number;
  businesses:         LaundryBusinessOption[];
  selectedBusinessId: string | null;
  errorMsg:           string | null;
}
let washModal: WashModal | null = null;

interface HehlerOrderInfo {
  id:             string;
  items:          { itemKey: string; itemType: string; quantity: number }[];
  payoutCents:    string;
  payoutCurrency: 'NOX' | 'BLACK_MONEY';
  startedAt:      string;
  payoutAt:       string;
  status:         string;
}
let hehlerOrders: HehlerOrderInfo[] = [];

interface ConsumableUseModal {
  itemType:  string;
  targetId:  string | null;
  errorMsg:  string | null;
}
let consumableUseModal: ConsumableUseModal | null = null;

// ─── Helfer ───────────────────────────────────────────────────────────────────

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Anzeige-Name inkl. Spitzname (additiv) — zentrale Formatierung, siehe utils/hideout-format.ts. */
function propName(p: OwnedProperty): string {
  return formatHideoutName({ subType: p.subType, customName: p.customName, nickname: p.nickname }, formatWarehouseName(p.label));
}

/** NT-POLISH-01 (P1): Übergabe an die zentrale Immobilien-Auswahl (utils/property-select.ts). */
function toSelectItem(p: OwnedProperty): PropertySelectItem {
  return {
    id: p.id, label: p.label, subType: p.subType, customName: p.customName, nickname: p.nickname,
    isHideout: isHideoutProperty(p), freeSlots: p.slots - p.currentLoad,
  };
}

function renderPackUnitsBadge(totalUnits: number, cls: string): string {
  const cssVar = packBadgeCssVar(totalUnits);
  return `<span class="${cls}" style="color:var(${cssVar});border-color:var(${cssVar});">[${String(totalUnits)}]</span>`;
}

/**
 * Pack-Badge (falls Pack-Item) UND Item-Badges (Qualität/Reinheit/Variante) zusammen —
 * für die Pulver-Linie (strassenpulver/laborpulver/reinsubstanz) braucht es BEIDES: die
 * Pack-Anzeige UND das bestehende Reinheits-Badge (STRASSENWARE etc., renderItemBadges()).
 * Für blaettchen/kapselhuelsen liefert renderItemBadges() ohnehin leer, kein Verhaltensunterschied.
 */
function renderCombinedBadges(itemKey: string, totalUnits: number, cls: string): string {
  const packBadge = isPackItem(itemKey) ? renderPackUnitsBadge(totalUnits, cls) : '';
  const itemBadges = renderItemBadges(itemKey, cls);
  return [packBadge, itemBadges].filter((s) => s.length > 0).join(' ');
}

function fmtCountdown(ms: number): string {
  if (ms <= 0) return 'FERTIG';
  const s = Math.floor(ms / 1000);
  if (s >= 3600) return `${String(Math.floor(s / 3600))}h ${String(Math.floor((s % 3600) / 60))}m`;
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

function raidRiskLabel(riskPerFL: number): string {
  return `${(riskPerFL * 100).toFixed(0)}% / FL`;
}

function slotBarColor(load: number, total: number): string {
  const pct = total > 0 ? load / total : 0;
  if (pct >= 0.95) return 'var(--accent-red)';
  if (pct >= 0.80) return 'var(--accent-orange)';
  return 'var(--accent-green)';
}

function isHideoutProperty(p: OwnedProperty): boolean {
  return p.isLabor !== undefined;
}

function itemCategoryLabel(type: string): string {
  return ({
    VEHICLE:        'FAHRZEUGE',
    MATERIAL:       'MATERIALIEN',
    LABOR_INPUT:    'LABOR-ZUTATEN',
    LABOR_OUTPUT:   'LABOR-WAREN',
    LAB_EQUIPMENT:  'LABOR-EQUIPMENT',
    FENCE_GOODS:    'HEHLERWAREN',
    GARDEN:         'GEWÄCHSHAUS',
    GARDEN_OUTPUT:  'ERNTE',
    TOBACCO_WET:    'FEUCHTER TABAK',
    TOBACCO_DRIED:  'GETROCKNETER TABAK',
    TOBACCO_CUT:    'TABAKFEINSCHNITT',
    ROLLING_SUPPLY: 'ROLLMATERIAL',
    ROLLING_OUTPUT: 'TABAKWAREN',
  } as Record<string, string>)[type] ?? type;
}

const CATEGORY_ORDER = [
  'VEHICLE', 'MATERIAL',
  'LABOR_INPUT', 'LABOR_OUTPUT', 'LAB_EQUIPMENT',
  'FENCE_GOODS',
  'GARDEN', 'GARDEN_OUTPUT',
  'TOBACCO_WET', 'TOBACCO_DRIED', 'TOBACCO_CUT',
  'ROLLING_SUPPLY', 'ROLLING_OUTPUT',
];

function groupItemsByCategory(items: InventoryItem[]): [string, InventoryItem[]][] {
  const grouped = new Map<string, InventoryItem[]>();
  for (const item of items) {
    const list = grouped.get(item.itemType) ?? [];
    list.push(item);
    grouped.set(item.itemType, list);
  }
  const sorted: [string, InventoryItem[]][] = [];
  for (const cat of CATEGORY_ORDER) {
    const items = grouped.get(cat);
    if (items) sorted.push([cat, items]);
  }
  for (const [k, v] of grouped) {
    if (!CATEGORY_ORDER.includes(k)) sorted.push([k, v]);
  }
  return sorted;
}

function allProperties(): OwnedProperty[] {
  return [...(propsData?.warehouses ?? []), ...(propsData?.hideouts ?? [])];
}

function renderTransportContents(t: TransportInfo): string {
  if (!t.bundleItems || t.bundleItems.length === 0) return '';
  const expanded = expandedTransportDetails.has(t.id);
  return `
    <button class="lh-transport-details-btn" data-lh-transport-details="${esc(t.id)}">${expanded ? 'INHALT ▲' : 'INHALT ▼'}</button>
    <div class="lh-transport-details${expanded ? ' is-open' : ''}">
      ${t.bundleItems.map((item) => `
        <div class="lh-transport-detail-row">
          <span class="lh-transport-detail-name">${esc(formatItemKey(item.itemKey))} ${renderItemBadges(item.itemKey, 'lh-q-badge')}</span>
          <span class="lh-transport-detail-qty">&#215;${String(item.quantity)}</span>
        </div>`).join('')}
    </div>`;
}

// ─── Render: EINLAGERN-Modal (Fahrzeug → Lager) ──────────────────────────────

function renderUnloadModal(): string {
  if (!unloadModal) return '';
  const um = unloadModal;
  const allProps = allProperties();
  const targets  = allProps.filter((p) => p.isActive);

  if (targets.length === 0) {
    return `
      <div class="modal-backdrop" id="lh-unload-modal">
        <div class="modal modal--narrow">
          <div class="modal-header"><h2>EINLAGERN</h2><p class="modal-subtitle">Von: ${esc(um.vehicleLabel)}</p></div>
          <div class="modal-body"><p class="lh-modal-warn">Kein aktives Lager vorhanden.</p></div>
          <div class="modal-actions"><button class="btn" data-close-unload-modal>SCHLIESSEN</button></div>
        </div>
      </div>`;
  }

  const isOverfilled = um.occupiedSlots > um.capacity;
  // Die frühere Sonderregel "überfülltes Fahrzeug ignoriert Ziellager-Kapazität" wurde entfernt
  // (NT-STORAGE-ENFORCE-01) — Kapazität zählt jetzt immer, auch bei überfülltem Fahrzeug.
  const overflowHint = isOverfilled
    ? `<p class="lh-unload-overflow-hint">FAHRZEUG ÜBERFÜLLT (${String(um.occupiedSlots)}/${String(um.capacity)} SLOTS) — EINLAGERN BRAUCHT TROTZDEM FREIE ZIELLAGER-SLOTS</p>`
    : '';

  const targetOptions = renderPropertyOptions(targets.map(toSelectItem), { selectedId: um.toPropertyId });

  const itemsHtml = um.items.length === 0
    ? '<p class="lh-modal-warn" style="color:var(--text-muted)">Fahrzeug ist leer.</p>'
    : um.items.map((group) => {
        const dis = !group.selected;
        return `
        <div class="lh-unload-item">
          <div class="lh-unload-item-top">
            <input type="checkbox" class="lh-send-check" id="lh-ugrp-${esc(group.itemKey)}"
              data-lh-unload-check="${esc(group.itemKey)}"
              ${group.selected ? 'checked' : ''} />
            <label class="lh-send-item-label" for="lh-ugrp-${esc(group.itemKey)}">
              ${esc(formatItemKey(group.itemKey))} ${renderCombinedBadges(group.itemKey, group.totalQty, 'lh-q-badge')}
              <span class="lh-send-item-avail">&nbsp;(${isPackItem(group.itemKey) ? `${String(packCount(group.itemKey, group.totalQty))} PKT` : String(group.totalQty)} VERF.)</span>
            </label>
          </div>
          <div class="lh-unload-qty-ctrl${dis ? ' lh-unload-qty-ctrl--disabled' : ''}">
            <button class="lh-unload-qbtn lh-unload-qbtn--dec"
              data-lh-unload-dec="${esc(group.itemKey)}" ${dis ? 'disabled' : ''}>&#8722;</button>
            <span class="lh-unload-qval" data-lh-unload-qval="${esc(group.itemKey)}">${isPackItem(group.itemKey) ? `${String(packCount(group.itemKey, group.transferQty))} PKT` : String(group.transferQty)}</span>
            <button class="lh-unload-qbtn lh-unload-qbtn--inc"
              data-lh-unload-inc="${esc(group.itemKey)}" ${dis ? 'disabled' : ''}>&#43;</button>
            <button class="lh-unload-qbtn lh-unload-qbtn--max"
              data-lh-unload-max="${esc(group.itemKey)}" ${dis ? 'disabled' : ''}>MAX</button>
          </div>
        </div>`;
      }).join('');

  const hasSelection = um.items.some((g) => g.selected && g.transferQty > 0);

  return `
    <div class="modal-backdrop" id="lh-unload-modal">
      <div class="modal modal--narrow">
        <div class="modal-header">
          <h2>EINLAGERN</h2>
          <p class="modal-subtitle">Von: ${esc(um.vehicleLabel)} &mdash; FAHRZEUG</p>
        </div>
        <div class="modal-body">
          ${overflowHint}
          <div class="lh-modal-row">
            <label class="lh-modal-label">1. GEGENSTÄNDE WÄHLEN</label>
            <div class="lh-send-items">${itemsHtml}</div>
          </div>
          <div class="lh-modal-row">
            <label class="lh-modal-label">2. ZIELLAGER WÄHLEN</label>
            <select class="markt-select" id="lh-unload-target">${targetOptions}</select>
          </div>
          ${um.errorMsg ? `<p class="lh-modal-warn">${esc(um.errorMsg)}</p>` : ''}
        </div>
        <div class="modal-actions">
          <button class="btn lh-unload-confirm-btn" id="lh-confirm-unload" ${!hasSelection ? 'disabled' : ''}>EINLAGERN</button>
          <button class="btn" data-close-unload-modal>ABBRECHEN</button>
        </div>
      </div>
    </div>`;
}

// ─── Render: VERKAUFEN-Modal ──────────────────────────────────────────────────

function fmtNox(centsStr: string): string {
  const cents = BigInt(centsStr);
  const whole = cents / 100n;
  const frac  = cents % 100n;
  return `${String(whole)},${String(frac).padStart(2, '0')} ₦`;
}

function fmtBlackMoney(centsStr: string): string {
  const cents = BigInt(centsStr);
  const whole = cents / 100n;
  const frac  = cents % 100n;
  return `${String(whole)},${String(frac).padStart(2, '0')} S-₦`;
}

function fmtPayout(centsStr: string, currency: 'NOX' | 'BLACK_MONEY'): string {
  return currency === 'NOX' ? fmtNox(centsStr) : fmtBlackMoney(centsStr);
}

function fmtMinutes(minutes: number): string {
  if (minutes >= 60) return `${String(Math.round(minutes / 60))}H`;
  return `${String(minutes)}MIN`;
}

function renderSellModal(): string {
  if (!sellModal) return '';
  const sm = sellModal;
  const selectedItem = sm.selectedItemKey !== null
    ? sm.items.find((i) => i.itemKey === sm.selectedItemKey) ?? null
    : null;

  const pricePerUnit = selectedItem ? BigInt(selectedItem.pricePerUnit) : 0n;
  const totalEarned  = pricePerUnit * BigInt(sm.qty);

  const itemOptions = sm.items.length === 0
    ? '<option value="">– Keine verkaufbaren Items –</option>'
    : [
        '<option value="">– AUSWÄHLEN –</option>',
        ...sm.items.map((i) => {
          const priceLabel = fmtNox(i.pricePerUnit);
          return `<option value="${esc(i.itemKey)}" ${i.itemKey === sm.selectedItemKey ? 'selected' : ''}>${esc(i.label)} (${String(i.quantity)}) &mdash; ${esc(priceLabel)}/Stk.</option>`;
        }),
      ].join('');

  const canConfirm = selectedItem !== null && sm.qty > 0;

  return `
    <div class="modal-backdrop" id="lh-sell-modal">
      <div class="modal modal--narrow">
        <div class="modal-header">
          <h2>VERKAUFEN</h2>
          <p class="modal-subtitle">Von: ${esc(sm.propertyName)}</p>
        </div>
        <div class="modal-body">
          <div class="lh-modal-row">
            <label class="lh-modal-label">1. GEGENSTAND WÄHLEN</label>
            <select class="markt-select" id="lh-sell-item-select">${itemOptions}</select>
          </div>

          ${selectedItem ? `
          <div class="lh-modal-row">
            <label class="lh-modal-label">2. MENGE WÄHLEN</label>
            <div class="lh-sell-price-info">
              <span class="lh-sell-price-label">PREIS/STK.</span>
              <span class="lh-sell-price-val">${esc(fmtNox(selectedItem.pricePerUnit))}</span>
            </div>
            <div class="lh-sell-qty-row">
              <button class="lh-sell-qbtn lh-sell-qbtn--dec" id="lh-sell-qty-dec">-</button>
              <span class="lh-sell-qval" id="lh-sell-qty-display">${String(sm.qty)}</span>
              <button class="lh-sell-qbtn lh-sell-qbtn--inc" id="lh-sell-qty-inc">+</button>
              <button class="lh-sell-qbtn lh-sell-qbtn--max" id="lh-sell-qty-max">MAX</button>
            </div>
            <div class="lh-sell-total">
              <span class="lh-sell-total-label">GESAMT</span>
              <span class="lh-sell-total-val" id="lh-sell-total">${esc(fmtNox(totalEarned.toString()))}</span>
            </div>
          </div>` : ''}

          ${sm.errorMsg ? `<p class="lh-modal-warn">${esc(sm.errorMsg)}</p>` : ''}
        </div>
        <div class="modal-actions">
          <button class="btn lh-sell-confirm-btn" id="lh-confirm-sell" ${!canConfirm ? 'disabled' : ''}>VERKAUFEN</button>
          <button class="btn" data-close-sell-modal>ABBRECHEN</button>
        </div>
      </div>
    </div>`;
}

// ─── Render: AN HEHLER-Modal (NT-HEHLER-KANAL-01) ────────────────────────────

function renderHehlerModal(): string {
  if (!hehlerModal) return '';
  const hm = hehlerModal;

  const itemsHtml = hm.items.length === 0
    ? '<p class="lh-modal-warn" style="color:var(--text-muted)">Keine Hehlerware im Lager.</p>'
    : hm.items.map((item) => `
        <div class="lh-send-item">
          <input type="checkbox" class="lh-send-check" id="lh-hehler-item-${esc(item.itemKey)}"
            data-lh-hehler-check="${esc(item.itemKey)}" ${item.selected ? 'checked' : ''} />
          <label class="lh-send-item-label" for="lh-hehler-item-${esc(item.itemKey)}">
            ${esc(formatItemKey(item.itemKey))} ${renderItemBadges(item.itemKey, 'lh-q-badge')}
            <span class="lh-send-item-avail">(${String(item.available)})</span>
          </label>
          <div class="lh-send-qty-ctrl${!item.selected ? ' lh-send-qty-ctrl--disabled' : ''}">
            <button class="lh-send-qbtn lh-send-qbtn--dec" data-lh-hehler-dec="${esc(item.itemKey)}" ${!item.selected ? 'disabled' : ''}>&#8722;</button>
            <input type="number" class="lh-send-qty-input" data-lh-hehler-qty-input="${esc(item.itemKey)}"
              value="${String(item.qty)}" min="1" max="${String(item.available)}" ${!item.selected ? 'disabled' : ''} />
            <button class="lh-send-qbtn lh-send-qbtn--inc" data-lh-hehler-inc="${esc(item.itemKey)}" ${!item.selected ? 'disabled' : ''}>&#43;</button>
            <button class="lh-send-qbtn lh-send-qbtn--max" data-lh-hehler-max="${esc(item.itemKey)}" ${!item.selected ? 'disabled' : ''}>MAX</button>
          </div>
          ${item.itemKey === BARGELD_BUENDEL_KEY ? `<button class="lh-wash-link-btn" data-lh-open-wash="${esc(hm.propertyId)}" data-lh-wash-avail="${String(item.available)}">STATTDESSEN WASCHEN (GELDWÄSCHE, NOX)</button>` : ''}
        </div>`).join('');

  const hasSelection = hm.items.some((i) => i.selected && i.qty > 0);

  return `
    <div class="modal-backdrop" id="lh-hehler-modal">
      <div class="modal modal--narrow">
        <div class="modal-header">
          <h2>AN HEHLER</h2>
          <p class="modal-subtitle">Von: ${esc(hm.propertyName)} &mdash; Ware verlässt sofort das Lager, Auszahlung folgt nach einer Bearbeitungszeit.</p>
        </div>
        <div class="modal-body">
          <div class="lh-modal-row">
            <label class="lh-modal-label">GEGENSTÄNDE WÄHLEN</label>
            <div class="lh-send-items">${itemsHtml}</div>
          </div>
          ${hm.errorMsg ? `<p class="lh-modal-warn">${esc(hm.errorMsg)}</p>` : ''}
        </div>
        <div class="modal-actions">
          <button class="btn lh-hehler-confirm-btn" id="lh-confirm-hehler" ${!hasSelection ? 'disabled' : ''}>AN HEHLER ÜBERGEBEN</button>
          <button class="btn" data-close-hehler-modal>ABBRECHEN</button>
        </div>
      </div>
    </div>`;
}

// ─── Render: Bargeld-Bündel WASCHEN-Modal (E4, Item-Input-Pfad Geldwäsche) ───

function renderWashModal(): string {
  if (!washModal) return '';
  const wm = washModal;

  if (wm.businesses.length === 0) {
    return `
      <div class="modal-backdrop" id="lh-wash-modal">
        <div class="modal modal--narrow">
          <div class="modal-header"><h2>WASCHEN</h2></div>
          <div class="modal-body"><p class="lh-modal-warn">Kein Geldwäsche-Betrieb vorhanden. Der Hehler bleibt möglich.</p></div>
          <div class="modal-actions"><button class="btn" data-close-wash-modal>SCHLIESSEN</button></div>
        </div>
      </div>`;
  }

  const businessOptions = wm.businesses
    .map((b) => `<option value="${esc(b.id)}" ${b.id === wm.selectedBusinessId ? 'selected' : ''} ${b.status === 'FROZEN' ? 'disabled' : ''}>${esc(formatItemKey(b.type.toLowerCase()))} (${esc(b.status)})</option>`)
    .join('');

  return `
    <div class="modal-backdrop" id="lh-wash-modal">
      <div class="modal modal--narrow">
        <div class="modal-header">
          <h2>WASCHEN</h2>
          <p class="modal-subtitle">Bargeld-Bündel über einen Geldwäsche-Betrieb waschen — zahlt NOX statt S-₦, dauert länger.</p>
        </div>
        <div class="modal-body">
          <div class="lh-modal-row">
            <label class="lh-modal-label">1. BETRIEB WÄHLEN</label>
            <select class="markt-select" id="lh-wash-business">${businessOptions}</select>
          </div>
          <div class="lh-modal-row">
            <label class="lh-modal-label">2. MENGE WÄHLEN</label>
            <div class="lh-sell-qty-row">
              <button class="lh-sell-qbtn lh-sell-qbtn--dec" id="lh-wash-qty-dec">-</button>
              <span class="lh-sell-qval" id="lh-wash-qty-display">${String(wm.qty)}</span>
              <button class="lh-sell-qbtn lh-sell-qbtn--inc" id="lh-wash-qty-inc">+</button>
              <button class="lh-sell-qbtn lh-sell-qbtn--max" id="lh-wash-qty-max">MAX</button>
            </div>
          </div>
          ${wm.errorMsg ? `<p class="lh-modal-warn">${esc(wm.errorMsg)}</p>` : ''}
        </div>
        <div class="modal-actions">
          <button class="btn lh-hehler-confirm-btn" id="lh-confirm-wash">WASCHEN</button>
          <button class="btn" data-close-wash-modal>ABBRECHEN</button>
        </div>
      </div>
    </div>`;
}

// ─── Render: Aktive Hehler-Aufträge (Countdown in-place, Muster wie Transporte) ─

function renderActiveHehlerOrdersSection(): string {
  const active = hehlerOrders.filter((o) => o.status === 'IN_PROGRESS');
  if (active.length === 0) return '';

  const now = Date.now();
  const rows = active.map((o) => {
    const remaining = Math.max(0, new Date(o.payoutAt).getTime() - now);
    const elapsed    = now - new Date(o.startedAt).getTime();
    const total      = new Date(o.payoutAt).getTime() - new Date(o.startedAt).getTime();
    const pct        = total > 0 ? Math.min(100, Math.floor((elapsed / total) * 100)) : 100;
    const itemSummary = o.items.map((i) => `${esc(formatItemKey(i.itemKey))}&nbsp;&#215;${String(i.quantity)}`).join(', ');

    return `
      <div class="lh-at-row">
        <div class="lh-at-info">
          <span class="lh-at-item">${itemSummary}</span>
          <span class="lh-at-arrow">&#10140;</span>
          <span class="lh-at-dest">${esc(fmtPayout(o.payoutCents, o.payoutCurrency))}</span>
        </div>
        <div class="lh-at-progress">
          <div class="lh-at-bar"><div class="lh-at-fill" style="width:${String(pct)}%;"></div></div>
          <span class="lh-at-time" data-lh-hehler-order="${esc(o.id)}">${fmtCountdown(remaining)}</span>
          <button class="lh-transport-skip-btn" data-lh-hehler-skip="${esc(o.id)}">${String(HEHLER_SKIP_COST_SCHEINE_DISPLAY)}&nbsp;SCHEINE&nbsp;-&nbsp;SOFORT</button>
        </div>
      </div>`;
  }).join('');

  return `
    <div class="lh-at-section">
      <div class="lh-at-header">
        <span class="lh-at-title">AKTIVE HEHLER-AUFTRÄGE</span>
        <span class="lh-at-slots">${String(active.length)}&nbsp;/&nbsp;${String(HEHLER_MAX_ACTIVE_ORDERS_DISPLAY)}&nbsp;SLOTS</span>
      </div>
      ${rows}
    </div>`;
}

// ─── Render: Fahrzeug-Inventar-Karte ─────────────────────────────────────────

function renderVehicleInventoryCards(): string {
  if (vehicleInventories.length === 0) {
    return `<p class="lh-empty-inv" style="padding:var(--space-4) var(--space-4);">Kein Fahrzeug-Inventar vorhanden.</p>`;
  }

  const cards = vehicleInventories.map((v) => {
    const pct          = v.capacity > 0 ? Math.floor((v.occupiedSlots / v.capacity) * 100) : 0;
    const color        = slotBarColor(v.occupiedSlots, v.capacity);
    const isOverfilled = v.occupiedSlots > v.capacity;
    const isExpanded   = expandedVehicleCards.has(v.vehicleId);

    const grouped = new Map<string, { totalQty: number; slotCount: number }>();
    for (const s of v.slots) {
      const key = s.itemKey;
      if (key === null) continue;
      const entry = grouped.get(key) ?? { totalQty: 0, slotCount: 0 };
      entry.totalQty += s.quantity ?? 1;
      entry.slotCount += 1;
      grouped.set(key, entry);
    }
    const itemsHtml = Array.from(grouped.entries()).map(([key, g]) => `
        <div class="lh-inv-line">
          <span class="lh-inv-qty">${isPackItem(key) ? String(packCount(key, g.totalQty)) : String(g.totalQty)}&times;</span>
          <span class="lh-inv-key">${esc(formatItemKey(key))} ${renderCombinedBadges(key, g.totalQty, 'lh-q-badge')}</span>
          <span class="lh-inv-slots">[${String(g.slotCount)}&nbsp;SLOTS]</span>
        </div>`).join('');

    return `
      <div class="lh-card${isExpanded ? ' lh-card--expanded' : ''}">
        <div class="lh-card-toggle" data-lh-vehicle-toggle="${esc(v.vehicleId)}">
          <div class="lh-card-toggle-left">
            <span class="lh-card-name">${esc(v.label.toUpperCase())}</span>
            <span class="lh-card-type-badge">FAHRZEUG</span>
            ${isOverfilled ? '<span class="lh-badge lh-badge--warn">ÜBERFÜLLT</span>' : ''}
          </div>
          <div class="lh-card-toggle-right">
            <div class="lh-slot-row">
              <div class="lh-slot-track">
                <div class="lh-slot-fill" style="width:${String(Math.min(pct, 100))}%;background:${color};"></div>
              </div>
              <span class="lh-slot-label">${String(v.occupiedSlots)}&nbsp;/&nbsp;${String(v.capacity)}&nbsp;SLOTS</span>
            </div>
            <span class="lh-toggle-arrow" aria-hidden="true">${isExpanded ? '[-]' : '[+]'}</span>
          </div>
        </div>
        <div class="lh-card-body">
          ${v.occupiedSlots > 0 ? `
            <div class="lh-vehicle-inv-items">
              ${itemsHtml}
            </div>` : `<p class="lh-empty-inv">Leer</p>`}
          <div class="lh-card-actions">
            <button class="lh-unload-btn" data-lh-unload="${esc(v.vehicleId)}">EINLAGERN</button>
          </div>
        </div>
      </div>`;
  }).join('');

  return `<div class="lh-list">${cards}</div>`;
}

// ─── Render: VERSENDEN-Modal ──────────────────────────────────────────────────

function renderSendModal(): string {
  if (!sendModal) return '';
  const sm = sendModal;
  const fromProp = allProperties().find((p) => p.id === sm.fromPropertyId);
  if (!fromProp) return '';

  const fromName = propName(fromProp).toUpperCase();
  const others   = allProperties().filter((p) => p.id !== sm.fromPropertyId && p.isActive);

  // Fahrzeuge aus Garage (PlayerVehicle) + Legacy Inventar
  // Dealer-zugewiesene Fahrzeuge werden gefiltert (nicht für Transporte verfügbar)
  const allGarageVehicles = [
    ...(propsData?.garage.vehicles ?? []),
    ...(propsData?.garage.baseVehicles ?? []),
  ];
  // busyCount = Fahrzeuge aktuell in Transport/Schmuggel/Job-Einsatz (NT-STORAGE-ENFORCE-01 Bug B) —
  // vorher wurde hier nur dealerAssignedCount abgezogen, ein bereits unterwegs befindliches
  // Fahrzeug erschien fälschlich als wählbar (Backend hätte es dann erst beim Absenden abgelehnt).
  // propertyAssignedCount (NT-STORAGE-UPGRADE-01) wird hier ebenfalls ausgeschlossen — konservativ,
  // da diese Modal-Auswahl nicht weiß, ob fromPropertyId der zugewiesenen Property entspricht
  // (Backend würde ein falsch gewähltes zugewiesenes Fahrzeug korrekt ablehnen, aber lieber gar
  // nicht erst als wählbar zeigen als einen Reject nach Klick zu riskieren).
  const garageVehicles = allGarageVehicles
    .map((v) => ({ ...v, availableQty: v.quantity - v.dealerAssignedCount - v.propertyAssignedCount - v.busyCount }))
    .filter((v) => v.availableQty > 0);
  const vehicles = garageVehicles.length > 0
    ? garageVehicles.map((v) => ({ vehicleKey: v.vehicleKey, quantity: v.availableQty, isSlotFree: v.isSlotFree, dealerAssignedCount: 0, propertyAssignedCount: 0, busyCount: 0 }))
    : fromProp.inventoryItems.filter((i) => i.itemType === 'VEHICLE').map((i) => ({ vehicleKey: i.itemKey, quantity: i.quantity, isSlotFree: false, dealerAssignedCount: 0, propertyAssignedCount: 0, busyCount: 0 }));

  if (vehicles.length === 0) {
    return `
      <div class="modal-backdrop" id="lh-send-modal">
        <div class="modal modal--narrow">
          <div class="modal-header"><h2>VERSENDEN</h2><p class="modal-subtitle">Von: ${esc(fromName)}</p></div>
          <div class="modal-body"><p class="lh-modal-warn">Kein Fahrzeug in der Garage. Kaufe ein Fahrzeug im <a href="/business/markt">Markt</a>.</p></div>
          <div class="modal-actions"><button class="btn" data-close-send-modal>SCHLIESSEN</button></div>
        </div>
      </div>`;
  }

  if (others.length === 0) {
    return `
      <div class="modal-backdrop" id="lh-send-modal">
        <div class="modal modal--narrow">
          <div class="modal-header"><h2>VERSENDEN</h2><p class="modal-subtitle">Von: ${esc(fromName)}</p></div>
          <div class="modal-body"><p class="lh-modal-warn">Kein weiteres Lager vorhanden.</p></div>
          <div class="modal-actions"><button class="btn" data-close-send-modal>SCHLIESSEN</button></div>
        </div>
      </div>`;
  }

  // NT-POLISH-01 (P2): Reihenfolge Fahrzeug → Zielort → Gegenstände, beide mit Platzhalter
  // (kein still vorausgewähltes erstes Fahrzeug/Lager mehr).
  const vehicleOptions = `<option value="" disabled${!sm.vehicleKey ? ' selected' : ''}>— FAHRZEUG WÄHLEN —</option>` + vehicles
    .map((v) => `<option value="${esc(v.vehicleKey)}" ${v.vehicleKey === sm.vehicleKey ? 'selected' : ''}>${esc(formatItemKey(v.vehicleKey))} (&#215;${String(v.quantity)}${v.isSlotFree ? ' ·slot-frei' : ''})</option>`)
    .join('');

  const targetOptions = renderPropertyOptions(others.map(toSelectItem), {
    selectedId: sm.toPropertyId, placeholder: '— ZIELORT WÄHLEN —',
  });

  const sendableItems = sm.items.filter((i) => i.itemType !== 'VEHICLE');

  function renderSendItemRow(item: SendItem): string {
    return `
        <div class="lh-send-item">
          <input type="checkbox" class="lh-send-check" id="lh-item-${esc(item.itemKey)}"
            data-lh-item-check="${esc(item.itemKey)}" data-lh-type="${esc(item.itemType)}"
            ${item.selected ? 'checked' : ''} />
          <label class="lh-send-item-label" for="lh-item-${esc(item.itemKey)}">
            ${esc(formatItemKey(item.itemKey))} ${renderCombinedBadges(item.itemKey, item.sheetsAvailable ?? 0, 'lh-q-badge')}
            <span class="lh-send-item-avail">(${String(item.available)}${isPackItem(item.itemKey) ? ' PKT' : ''})</span>
          </label>
          <div class="lh-send-qty-ctrl${!item.selected ? ' lh-send-qty-ctrl--disabled' : ''}">
            <button class="lh-send-qbtn lh-send-qbtn--dec" data-lh-item-dec="${esc(item.itemKey)}" ${!item.selected ? 'disabled' : ''}>&#8722;</button>
            <input type="number" class="lh-send-qty-input" data-lh-qty-input="${esc(item.itemKey)}"
              value="${String(item.qty)}" min="1" max="${String(item.available)}" ${!item.selected ? 'disabled' : ''} />
            <button class="lh-send-qbtn lh-send-qbtn--inc" data-lh-item-inc="${esc(item.itemKey)}" ${!item.selected ? 'disabled' : ''}>&#43;</button>
            <button class="lh-send-qbtn lh-send-qbtn--max" data-lh-item-max="${esc(item.itemKey)}" ${!item.selected ? 'disabled' : ''}>MAX</button>
          </div>
        </div>`;
  }

  // Feste Kategorie-Sektionen (nicht collapsible), Reihenfolge = LAGER_KATEGORIE_ORDER,
  // unbekannte Kategorien ans Ende/alphabetisch, leere Kategorien werden nicht gerendert.
  const presentKategorien = new Set(sendableItems.map((i) => getLagerKategorie(i.itemType)));
  const orderedKategorien = [
    ...LAGER_KATEGORIE_ORDER.filter((k) => presentKategorien.has(k)),
    ...[...presentKategorien].filter((k) => !LAGER_KATEGORIE_ORDER.includes(k)).sort(),
  ];

  const itemsHtml = sendableItems.length === 0
    ? '<p class="lh-modal-warn" style="color:var(--text-muted)">Keine verschickbaren Waren im Lager.</p>'
    : orderedKategorien.map((kat) => {
        const items = sendableItems.filter((i) => getLagerKategorie(i.itemType) === kat);
        return `
        <div class="lh-send-cat-group">
          <p class="lh-send-cat-header">${esc(LAGER_KATEGORIE_LABEL[kat] ?? kat)}</p>
          ${items.map(renderSendItemRow).join('')}
        </div>`;
      }).join('');

  const hasSelection = sm.items.some((i) => i.selected && i.qty > 0);
  const canSend      = hasSelection && sm.vehicleKey !== '' && sm.toPropertyId !== '';

  return `
    <div class="modal-backdrop" id="lh-send-modal">
      <div class="modal modal--narrow">
        <div class="modal-header">
          <h2>VERSENDEN</h2>
          <p class="modal-subtitle">Von: ${esc(fromName)}</p>
        </div>
        <div class="modal-body">
          <div class="lh-modal-row">
            <label class="lh-modal-label">1. FAHRZEUG WÄHLEN</label>
            <select class="markt-select" id="lh-send-vehicle">${vehicleOptions}</select>
          </div>

          <div class="lh-modal-row">
            <label class="lh-modal-label">2. ZIELORT WÄHLEN</label>
            <select class="markt-select" id="lh-send-target">${targetOptions}</select>
          </div>

          <div class="lh-modal-row">
            <label class="lh-modal-label">3. GEGENSTÄNDE WÄHLEN</label>
            <div class="lh-send-items">${itemsHtml}</div>
          </div>

          <p class="lh-modal-hint" id="lh-send-duration-preview">${esc(sendDurationPreviewText || sendDurationDefaultHint(hasSelection))}</p>

          ${sm.errorMsg ? `<p class="lh-modal-warn">${esc(sm.errorMsg)}</p>` : ''}
        </div>
        <div class="modal-actions">
          <button class="btn btn--primary" id="lh-confirm-send" ${!canSend ? 'disabled' : ''}>VERSENDEN</button>
          <button class="btn" data-close-send-modal>ABBRECHEN</button>
        </div>
      </div>
    </div>`;
}

// NT-TRANSPORT-BELADUNG-01 Rev. 2: Dauer-Vorschau vor dem Absenden. Reiner Anzeige-Text-Zustand
// (sendDurationPreviewText), per Debounce nachgeladen und in-place gepatcht — kein Full-Re-Render
// bei jeder Vehicle-/Item-/Mengenänderung (Muster wie updateSupplyModalDerivedDom() in crimeOps.ts).
function sendDurationDefaultHint(hasSelection: boolean, sm: SendModal | null = sendModal): string {
  if (sm && (sm.vehicleKey === '' || sm.toPropertyId === '')) {
    return 'Wähle Fahrzeug und Zielort für eine Zeitschätzung.';
  }
  return hasSelection
    ? 'Dauer wird berechnet...'
    : 'Wähle mindestens einen Gegenstand für eine Zeitschätzung.';
}

function buildSendPreviewItems(sm: SendModal): { itemKey: string; itemType: string; quantity: number }[] {
  return sm.items
    .filter((i) => i.selected && i.qty > 0)
    .map((i) => ({
      itemKey: i.itemKey,
      itemType: i.itemType,
      quantity: isPackItem(i.itemKey) && i.sheetsAvailable !== undefined
        ? packsToUnits(i.itemKey, i.qty, i.sheetsAvailable)
        : i.qty,
    }));
}

function patchSendDurationPreviewDom(): void {
  if (!sendModal) return;
  const el = document.getElementById('lh-send-duration-preview');
  if (!el) return;
  const hasSelection = sendModal.items.some((i) => i.selected && i.qty > 0);
  el.textContent = sendDurationPreviewText || sendDurationDefaultHint(hasSelection);
}

/** NT-POLISH-01 (P2): VERSENDEN erst aktiv, wenn Fahrzeug, Zielort und mind. ein Gegenstand gewählt sind. */
function updateSendConfirmState(): void {
  if (!sendModal) return;
  const confirmBtn = document.getElementById('lh-confirm-send') as HTMLButtonElement | null;
  if (!confirmBtn) return;
  const hasValid = sendModal.items.some((item) => {
    if (!item.selected) return false;
    const inp = document.querySelector<HTMLInputElement>(`[data-lh-qty-input="${item.itemKey}"]`);
    const v = inp ? parseInt(inp.value, 10) : item.qty;
    return !isNaN(v) && v > 0;
  });
  confirmBtn.disabled = !hasValid || sendModal.vehicleKey === '' || sendModal.toPropertyId === '';
}

function scheduleSendDurationPreview(): void {
  sendDurationPreviewText = '';
  patchSendDurationPreviewDom();

  if (sendPreviewDebounceTimer !== null) clearTimeout(sendPreviewDebounceTimer);
  sendPreviewDebounceTimer = setTimeout(() => { void refreshSendDurationPreview(); }, 300);
}

async function refreshSendDurationPreview(): Promise<void> {
  // Referenz auf DIESES Modal-Objekt einfangen — schützt nicht nur gegen "Modal inzwischen
  // geschlossen" (sendModal === null), sondern auch gegen "Modal geschlossen UND ein neues
  // geöffnet, bevor diese Antwort da ist" (sendModal zeigt dann auf ein anderes Objekt). Ein
  // reiner !sendModal-Check nach dem await würde TS/ESLint zudem als unreachable markieren, da
  // die Narrowing aus dem Funktionsstart über den await hinweg erhalten bleibt.
  const modalRef = sendModal;
  if (!modalRef) return;
  const items = buildSendPreviewItems(modalRef);
  if (items.length === 0) return; // Hinweistext ("wähle mind. einen Gegenstand") bleibt stehen
  if (modalRef.vehicleKey === '' || modalRef.toPropertyId === '') return; // NT-POLISH-01 (P2)

  const vehicleKey = modalRef.vehicleKey;
  const seq = ++sendPreviewRequestSeq;
  try {
    // NT-VIERTEL-02: fromPropertyId/toPropertyId für die echte Distanz-Berechnung mitschicken.
    const preview = await api.post<TransportPreview>('/transport/preview', {
      vehicleKey, items,
      fromPropertyId: modalRef.fromPropertyId,
      toPropertyId:   modalRef.toPropertyId,
    });
    if (seq !== sendPreviewRequestSeq || sendModal !== modalRef) return;
    const entfernungLabel = ` · Entfernung ${String(preview.steps)} Straße${preview.steps === 1 ? '' : 'n'}`;
    sendDurationPreviewText = `Dauer ca. ${String(preview.totalMinutes)} Min. (Verladung ${String(preview.loadingMinutes)} / Fahrt ${String(preview.travelMinutes)} / Abladung ${String(preview.unloadingMinutes)})${entfernungLabel}`;
  } catch {
    if (seq !== sendPreviewRequestSeq || sendModal !== modalRef) return;
    sendDurationPreviewText = 'Dauer konnte nicht berechnet werden.';
  }
  patchSendDurationPreviewDom();
}

// ─── Render: Transfer-Modal ───────────────────────────────────────────────────

function renderTransferModal(): string {
  if (!transferModal) return '';
  const tm = transferModal;
  const others = allProperties().filter((p) => p.id !== tm.fromPropertyId && p.isActive);
  const fromProp = allProperties().find((p) => p.id === tm.fromPropertyId);

  // Fahrzeuge im Quelllager
  const vehicles = fromProp?.inventoryItems.filter((i) => i.itemType === 'VEHICLE') ?? [];

  if (others.length === 0) {
    return `
      <div class="modal-backdrop" id="lh-transfer-modal">
        <div class="modal modal--narrow">
          <div class="modal-header"><h2>VERSCHIEBEN</h2></div>
          <div class="modal-body"><p class="lh-modal-warn">Kein weiteres Lager vorhanden.</p></div>
          <div class="modal-actions"><button class="btn" data-close-lh-modal>SCHLIESSEN</button></div>
        </div>
      </div>`;
  }

  if (vehicles.length === 0) {
    return `
      <div class="modal-backdrop" id="lh-transfer-modal">
        <div class="modal modal--narrow">
          <div class="modal-header"><h2>VERSCHIEBEN</h2></div>
          <div class="modal-body"><p class="lh-modal-warn">Kein Fahrzeug im Quelllager. Im <a href="/business/markt">Markt</a> kaufen.</p></div>
          <div class="modal-actions"><button class="btn" data-close-lh-modal>SCHLIESSEN</button></div>
        </div>
      </div>`;
  }

  const toOptions = renderPropertyOptions(others.map(toSelectItem), { selectedId: tm.toPropertyId });
  const vehicleOptions = vehicles.map((v) => `<option value="${esc(v.itemKey)}" ${v.itemKey === tm.vehicleKey ? 'selected' : ''}>${esc(formatItemKey(v.itemKey))} (×${String(v.quantity)})</option>`).join('');

  return `
    <div class="modal-backdrop" id="lh-transfer-modal">
      <div class="modal modal--narrow">
        <div class="modal-header">
          <h2>VERSCHIEBEN</h2>
          <p class="modal-subtitle">${esc(tm.itemKey.toUpperCase())} · ${String(tm.availableQty)} verfügbar</p>
        </div>
        <div class="modal-body">
          <div class="lh-modal-row">
            <label class="lh-modal-label">MENGE</label>
            <div class="markt-qty-row">
              <button class="markt-qty-btn" data-lh-qty="-1">-</button>
              <span class="markt-qty-val" id="lh-qty-display">${String(tm.qty)}</span>
              <button class="markt-qty-btn" data-lh-qty="+1">+</button>
            </div>
          </div>
          <div class="lh-modal-row">
            <label class="lh-modal-label">FAHRZEUG</label>
            <select class="markt-select" id="lh-vehicle-select">${vehicleOptions}</select>
          </div>
          <div class="lh-modal-row">
            <label class="lh-modal-label">ZIELLAGER</label>
            <select class="markt-select" id="lh-target-select">${toOptions}</select>
          </div>
        </div>
        <div class="modal-actions">
          <button class="btn btn--primary" id="lh-confirm-transfer">TRANSPORT STARTEN</button>
          <button class="btn" data-close-lh-modal>ABBRECHEN</button>
        </div>
      </div>
    </div>`;
}

// ─── Render: Property-Card (Dropdown) ────────────────────────────────────────

// ─── Render: Lager-Ausbau + Fahrzeugslot (NT-STORAGE-UPGRADE-01, nur Lager) ──────

function renderStorageUpgradeSection(p: OwnedProperty): string {
  if (p.storageUpgradeLevel === undefined) return '';
  const level = p.storageUpgradeLevel;
  const maxLevel = p.maxStorageLevel ?? level;

  if (p.storageUpgradeCompletesAt) {
    const remaining = Math.max(0, new Date(p.storageUpgradeCompletesAt).getTime() - Date.now());
    return `
      <div class="lh-upgrade-row">
        <span class="lh-upgrade-label">AUSBAU STUFE ${String(level + 1)} LÄUFT</span>
        <span class="lh-upgrade-time" data-lh-upgrade="${esc(p.id)}">${fmtCountdown(remaining)}</span>
      </div>`;
  }

  if (level >= maxLevel) {
    return `
      <div class="lh-upgrade-row">
        <span class="lh-upgrade-label">AUSBAUSTUFE ${String(level)}&nbsp;/&nbsp;${String(maxLevel)} (MAX)</span>
      </div>`;
  }

  const costLabel = p.nextUpgradeCostCents ? fmtNox(p.nextUpgradeCostCents) : '–';
  const minutesLabel = p.nextUpgradeMinutes !== null && p.nextUpgradeMinutes !== undefined ? fmtMinutes(p.nextUpgradeMinutes) : '–';
  return `
    <div class="lh-upgrade-row">
      <span class="lh-upgrade-label">AUSBAUSTUFE ${String(level)}&nbsp;/&nbsp;${String(maxLevel)}</span>
      <button class="lh-upgrade-btn" data-lh-upgrade-start="${esc(p.id)}">AUSBAUEN&nbsp;(${esc(costLabel)}&nbsp;&mdash;&nbsp;${esc(minutesLabel)})</button>
    </div>`;
}

function renderVehicleSlotSection(p: OwnedProperty): string {
  if (p.hasVehicleSlot === undefined) return '';

  if (!p.hasVehicleSlot) {
    return `
      <div class="lh-vslot-row">
        <button class="lh-vslot-btn" data-lh-vslot-buy="${esc(p.id)}">STELLPLATZ KAUFEN&nbsp;(${esc(fmtNox(VEHICLE_SLOT_COST_CENTS_DISPLAY))})</button>
      </div>`;
  }

  if (p.assignedVehicleId) {
    return `
      <div class="lh-vslot-row">
        <span class="lh-vslot-label">STELLPLATZ: ${esc(formatItemKey(p.assignedVehicleKey ?? ''))}</span>
        <button class="lh-vslot-btn lh-vslot-btn--unassign" data-lh-vslot-unassign="${esc(p.id)}">ABMELDEN</button>
      </div>`;
  }

  // NT-VIERTEL-FIX-01 (V1): der Lager-Slot ist ein normaler Stellplatz — jedes garagenpflichtige
  // Fahrzeug, das gerade nicht unterwegs ist, lässt sich hierher umstellen (auch Dealer-/Job-
  // Fahrzeuge). Fahrzeuge ohne Stellplatz zuerst.
  const freeVehicles = vehicleList
    .filter((v) => v.requiresStellplatz && v.canMoveStellplatz)
    .sort((a, b) => Number(a.stellplatzPropertyId !== null) - Number(b.stellplatzPropertyId !== null));
  const options = freeVehicles.length === 0
    ? '<option value="">– Kein Fahrzeug verfügbar –</option>'
    : freeVehicles.map((v) => `<option value="${esc(v.id)}">${esc(v.label)}${v.stellplatzPropertyId === null ? ' (ohne Stellplatz)' : ' (umstellen)'}</option>`).join('');

  return `
    <div class="lh-vslot-row">
      <span class="lh-vslot-label">STELLPLATZ FREI</span>
      <select class="markt-select" data-lh-vslot-select="${esc(p.id)}">${options}</select>
      <button class="lh-vslot-btn" data-lh-vslot-assign="${esc(p.id)}" ${freeVehicles.length === 0 ? 'disabled' : ''}>ZUWEISEN</button>
    </div>`;
}

// ─── Render: Lager-Dedizierung (Pfand: NT-PFAND-LAGER-LOOPB-01, Werkstatt: NT-WERKSTATT-PUFFER-REAL-INVENTORY-01) ────────

// Reine Anzeige-Konstanten — Backend: pfand.constants.ts::PFAND_ITEM_KEY /
// werkstatt.definitions.ts::WERKSTATT_BUFFER_DEDICATION_KEY (kein Shared-Import).
const PFAND_ITEM_KEY_DISPLAY = 'pfandflasche';
const WERKSTATT_DEDICATION_KEY_DISPLAY = '__werkstatt_buffer__';

function renderDedicationSection(p: OwnedProperty): string {
  if (p.dedicatedItemKey) {
    const label =
      p.dedicatedItemKey === PFAND_ITEM_KEY_DISPLAY ? 'PFAND-LAGER' :
      p.dedicatedItemKey === WERKSTATT_DEDICATION_KEY_DISPLAY ? 'WERKSTATT-LAGER' :
      formatItemKey(p.dedicatedItemKey);
    return `
      <div class="lh-vslot-row">
        <span class="lh-vslot-label">DEDIZIERT: ${esc(label)}</span>
        <button class="lh-vslot-btn lh-vslot-btn--unassign" data-lh-undedicate="${esc(p.id)}">AUFHEBEN</button>
      </div>`;
  }

  const canDedicate = p.currentLoad === 0;
  if (!canDedicate) {
    return `
      <div class="lh-vslot-row">
        <button class="lh-vslot-btn" disabled>ERST LEEREN ZUM DEDIZIEREN</button>
      </div>`;
  }
  return `
    <div class="lh-vslot-row">
      <button class="lh-vslot-btn" data-lh-dedicate="${esc(p.id)}">ALS PFAND-LAGER DEDIZIEREN</button>
      <button class="lh-vslot-btn" data-lh-dedicate-werkstatt="${esc(p.id)}">ALS WERKSTATT-LAGER DEDIZIEREN</button>
    </div>`;
}

function renderPropertyCard(p: OwnedProperty): string {
  const isHideout    = isHideoutProperty(p);
  const typeLabel    = p.isLabor ? 'LABOR' : isHideout ? 'VERSTECK' : 'LAGER';
  const isExpanded   = expandedCards.has(p.id);
  const pct          = p.slots > 0 ? Math.floor((p.currentLoad / p.slots) * 100) : 0;
  const color        = slotBarColor(p.currentLoad, p.slots);
  const ordersForThis        = pendingOrders.filter((o) => o.targetPropertyId === p.id);
  const activeTransportsFrom = transportData?.active.filter((t) => t.fromPropertyId === p.id) ?? [];
  const activeTransportsTo   = transportData?.active.filter((t) => t.toPropertyId   === p.id) ?? [];

  // Legacy-Items und Leer-Slots ausblenden: Fahrzeuge → PlayerVehicle, Kontakte → PlayerContact
  const visibleItems = p.inventoryItems.filter((i) =>
    i.itemType !== 'VEHICLE' && !i.itemKey.startsWith('kontakt_') && i.quantity > 0,
  );
  const groups = groupItemsByCategory(visibleItems);

  const categoryGroupsHtml = groups.length === 0
    ? `<p class="lh-empty-inv">Leer</p>`
    : groups.map(([catType, items]) => `
        <div class="lh-cat-section">
          <h4 class="lh-cat-title">${itemCategoryLabel(catType)}</h4>
          <div class="lh-cat-items">
            ${items.map((i) => `
              <div class="lh-inv-line">
                <span class="lh-inv-qty">${isPackItem(i.itemKey) ? String(packCount(i.itemKey, i.quantity)) : String(i.quantity)}&times;</span>
                <span class="lh-inv-key">${esc(formatItemKey(i.itemKey))} ${renderCombinedBadges(i.itemKey, i.quantity, 'lh-q-badge')}${i.itemType === 'SMUGGLED_GOODS' ? ' <span class="lh-badge lh-badge--import">IMPORT</span>' : ''}</span>
                <span class="lh-inv-slots">[${String(i.slotCost)}&nbsp;SLOT${i.slotCost !== 1 ? 'S' : ''}]</span>
              </div>`).join('')}
          </div>
        </div>`).join('');

  return `
    <div class="lh-card${!p.isActive ? ' lh-card--inactive' : ''}${isExpanded ? ' lh-card--expanded' : ''}" id="lh-card-${esc(p.id)}">
      <div class="lh-card-toggle" data-lh-toggle="${esc(p.id)}">
        <div class="lh-card-toggle-left">
          <span class="lh-card-name">${esc(propName(p).toUpperCase())}</span>
          <span class="lh-card-type-badge">${typeLabel}</span>
          ${!p.isActive ? '<span class="lh-badge lh-badge--inactive">INAKTIV</span>' : ''}
        </div>
        <div class="lh-card-toggle-right">
          <div class="lh-slot-row">
            <div class="lh-slot-track">
              <div class="lh-slot-fill" style="width:${String(pct)}%;background:${color};"></div>
            </div>
            <span class="lh-slot-label">${String(p.currentLoad)}&nbsp;/&nbsp;${String(p.slots)}&nbsp;SLOTS</span>
          </div>
          <span class="lh-toggle-arrow" aria-hidden="true">${isExpanded ? '[-]' : '[+]'}</span>
        </div>
      </div>

      <div class="lh-card-body">
        <div class="lh-nickname-row">
          <input type="text" class="lh-nickname-input" data-lh-nickname-input="${esc(p.id)}" maxlength="30" placeholder="Kein Spitzname" value="${esc(p.nickname ?? '')}">
          <button class="btn lh-btn--sm" data-lh-save-nickname="${esc(p.id)}">SPEICHERN</button>
        </div>
        <div class="lh-card-actions lh-card-actions--top">
          <button class="lh-send-btn" data-lh-send="${esc(p.id)}">VERSENDEN</button>
          <button class="lh-sell-btn" data-lh-sell="${esc(p.id)}" data-lh-sell-name="${esc(propName(p).toUpperCase())}">VERKAUFEN</button>
          <button class="lh-hehler-btn" data-lh-hehler="${esc(p.id)}" data-lh-hehler-name="${esc(propName(p).toUpperCase())}">AN HEHLER</button>
        </div>
        ${categoryGroupsHtml}

        ${ordersForThis.length > 0 ? `
          <div class="lh-deliveries">
            <h4 class="lh-cat-title">EINGEHENDE LIEFERUNGEN</h4>
            ${ordersForThis.map((o) => `
              <div class="lh-delivery">
                <span class="lh-delivery-label">&#8594; ${esc(o.itemLabel.toUpperCase())}&nbsp;&#215;${String(o.quantity)}</span>
                <span class="lh-delivery-time" data-lh-order="${esc(o.id)}">${fmtCountdown(o.remainingMs)}</span>
              </div>`).join('')}
          </div>` : ''}

        ${activeTransportsFrom.length > 0 ? `
          <div class="lh-transports lh-transports--out">
            <h4 class="lh-cat-title lh-cat-title--out">AUSGEHENDE TRANSPORTE</h4>
            ${activeTransportsFrom.map((t) => `
              <div class="lh-transport">
                <span class="lh-transport-label">&#10140; ${esc(formatItemKey(t.itemKey))} ${renderItemBadges(t.itemKey, 'lh-q-badge')}&nbsp;&#215;${String(t.quantity)}&nbsp;&rarr;&nbsp;${esc(formatWarehouseName(t.toLabel))}</span>
                <span class="lh-transport-time" data-lh-transport="${esc(t.id)}">${fmtCountdown(t.remainingMs)}</span>
                ${renderTransportContents(t)}
              </div>`).join('')}
          </div>` : ''}

        ${activeTransportsTo.length > 0 ? `
          <div class="lh-transports lh-transports--in">
            <h4 class="lh-cat-title lh-cat-title--in">EINGEHENDE TRANSPORTE</h4>
            ${activeTransportsTo.map((t) => `
              <div class="lh-transport">
                <span class="lh-transport-label">&#10140; ${esc(formatItemKey(t.itemKey))} ${renderItemBadges(t.itemKey, 'lh-q-badge')}&nbsp;&#215;${String(t.quantity)}&nbsp;von&nbsp;${esc(formatWarehouseName(t.fromLabel))}</span>
                <span class="lh-transport-time" data-lh-transport="${esc(t.id)}">${fmtCountdown(t.remainingMs)}</span>
                ${renderTransportContents(t)}
              </div>`).join('')}
          </div>` : ''}

        <div class="lh-card-actions">
          <button class="lh-send-btn" data-lh-send="${esc(p.id)}">VERSENDEN</button>
          <button class="lh-sell-btn" data-lh-sell="${esc(p.id)}" data-lh-sell-name="${esc(propName(p).toUpperCase())}">VERKAUFEN</button>
          <button class="lh-hehler-btn" data-lh-hehler="${esc(p.id)}" data-lh-hehler-name="${esc(propName(p).toUpperCase())}">AN HEHLER</button>
        </div>

        ${!isHideout ? `
          <div class="lh-upgrade-section">
            ${renderStorageUpgradeSection(p)}
            ${renderVehicleSlotSection(p)}
            ${renderDedicationSection(p)}
          </div>` : ''}

        <div class="lh-meta">
          <span class="lh-meta-item">RAZZIA&nbsp;<span class="${isHideout || p.isLabor ? 'val-green' : 'val-orange'}">${raidRiskLabel(p.raidRiskPerFL)}</span></span>
        </div>
      </div>
    </div>`;
}

// ─── Render: Seite ────────────────────────────────────────────────────────────

// ─── Render: Garage-Sektion ───────────────────────────────────────────────────

function renderRoleBadge(role: string): string {
  switch (role) {
    case 'JOB':        return '<span class="lh-role-badge lh-role-badge--job">JOB-FAHRZEUG</span>';
    case 'DEALER':     return '<span class="lh-role-badge lh-role-badge--dealer">DEALER</span>';
    case 'TRANSPORT':  return '<span class="lh-role-badge lh-role-badge--transport">TRANSPORT</span>';
    case 'SMUGGLING':  return '<span class="lh-role-badge lh-role-badge--smuggling">SCHMUGGEL</span>';
    case 'AUFTRAG':    return '<span class="lh-role-badge lh-role-badge--auftrag">AUFTRAGSBRETT</span>';
    default:           return '<span class="lh-role-badge lh-role-badge--free">FREI</span>';
  }
}

/** VERKAUFEN-Button bzw. Inline-Bestätigung — nur für freie Fahrzeuge, das Backend prüft erneut. */
function renderVehicleSellControl(v: VehicleListItem): string {
  if (v.role !== 'FREE') return '';
  if (vehicleSellConfirmId === v.id) {
    return `
      <span class="lh-garage-sell-confirm">FÜR ${fmtNox(v.sellPriceCents)} VERKAUFEN?</span>
      <button class="lh-role-btn lh-role-btn--sell" data-lh-sell-vehicle-confirm="${esc(v.id)}">JA</button>
      <button class="lh-role-btn lh-role-btn--clear" data-lh-sell-vehicle-cancel="1">NEIN</button>`;
  }
  return `<button class="lh-role-btn lh-role-btn--sell" data-lh-sell-vehicle="${esc(v.id)}">VERKAUFEN</button>`;
}

function renderGarageSection(): string {
  const g = propsData?.garage;
  if (!g) return '';

  const hasVehicles = vehicleList.length > 0 || g.baseVehicles.length > 0;

  // Individuelle Fahrzeuge mit Slot-Bedarf aus vehicleList
  const baseVehicleKeys = new Set(g.baseVehicles.map((b) => b.vehicleKey));
  const individualSlotted = vehicleList.filter((v) => !baseVehicleKeys.has(v.vehicleKey));
  const individualBase    = vehicleList.filter((v) => baseVehicleKeys.has(v.vehicleKey));

  // NT-VIERTEL-FIX-01 (V1): jeder Platz gehört zu einer Immobilie — Heimatplatz je Fahrzeug anzeigen.
  const ownProps = [...(propsData?.warehouses ?? []), ...(propsData?.hideouts ?? [])];
  const stellplatzBadge = (v: VehicleListItem): string => {
    if (!v.stellplatzPropertyId) return '<span class="lh-role-badge lh-role-badge--homeless">OHNE STELLPLATZ</span>';
    const p = ownProps.find((x) => x.id === v.stellplatzPropertyId);
    const name = p ? formatPropertyDisplayName({ ...p, isHideout: isHideoutProperty(p) }) : 'Immobilie';
    return `<span class="lh-garage-row-loc">${esc(name.toUpperCase())}</span>`;
  };

  const slotRows = individualSlotted.map((v) => {
    const isFree = v.isAvailable;
    const btn = isFree
      ? `<button class="lh-role-btn" data-lh-set-job-vehicle="${esc(v.id)}">ALS JOB-FAHRZEUG SETZEN</button>`
      : v.role === 'JOB'
        ? `<button class="lh-role-btn lh-role-btn--clear" data-lh-clear-job-vehicle="1">FREIGEBEN</button>`
        : '';
    return `
      <div class="lh-garage-row">
        <span class="lh-garage-row-icon">&#9646;</span>
        <span class="lh-garage-row-name">${esc(v.label)}</span>
        ${stellplatzBadge(v)}
        ${renderRoleBadge(v.role)}
        ${btn}
        ${renderVehicleSellControl(v)}
      </div>`;
  }).join('');

  // Freie Slots
  const freeSlotRows = Array.from({ length: g.freeSlots }, () => `
    <div class="lh-garage-row lh-garage-row--free">
      <span class="lh-garage-row-icon">&#9647;</span>
      <span class="lh-garage-row-name" style="color:var(--text-muted)">FREIER STELLPLATZ</span>
    </div>`).join('');

  const baseRows = individualBase.map((v) => {
    const isFree = v.isAvailable;
    const btn = isFree
      ? `<button class="lh-role-btn" data-lh-set-job-vehicle="${esc(v.id)}">ALS JOB-FAHRZEUG SETZEN</button>`
      : v.role === 'JOB'
        ? `<button class="lh-role-btn lh-role-btn--clear" data-lh-clear-job-vehicle="1">FREIGEBEN</button>`
        : '';
    return `
      <div class="lh-garage-row">
        <span class="lh-garage-row-icon">&#9646;</span>
        <span class="lh-garage-row-name">${esc(v.label)}</span>
        <span class="lh-role-badge lh-role-badge--base">SLOT-FREI</span>
        ${renderRoleBadge(v.role)}
        ${btn}
        ${renderVehicleSellControl(v)}
      </div>`;
  }).join('');

  return `
    <div class="lh-garage-section">
      <div class="lh-garage-header">
        <h3 class="lh-garage-title">GARAGE</h3>
        <span class="lh-garage-slots-badge">${String(g.occupiedSlots)}&nbsp;/&nbsp;${String(g.totalSlots)}&nbsp;STELLPLÄTZE</span>
      </div>

      ${g.totalSlots === 0 && !hasVehicles ? `
        <p class="lh-garage-empty">Keine Stellplätze. Kaufe ein Versteck mit Garage oder einen Stellplatz für ein Lager.</p>
      ` : ''}

      ${individualSlotted.length > 0 || g.freeSlots > 0 ? `
        <div class="lh-garage-rows">
          ${slotRows}
          ${freeSlotRows}
        </div>
      ` : ''}

      ${individualBase.length > 0 ? `
        <div class="lh-garage-rows" style="margin-top:var(--space-2)">
          <h4 class="lh-cat-title">BASISFAHRZEUGE (SLOT-FREI)</h4>
          ${baseRows}
        </div>
      ` : ''}
    </div>`;
}

// NT-TRANSPORT-BELADUNG-01: Phasen-Label+Balken (Verladung/Fahrt/Abladung). Alt-Transporte ohne
// Phasen-Zeitpunkte (computeTransportPhaseUi() liefert dann null) fallen auf die bisherige,
// durchgehende "LIEFERUNG"-Anzeige (gelb) zurück — kein Bruch für laufende Bestandstransporte.
function renderTransportPhaseBar(t: TransportInfo, now: number): string {
  const phaseUi = computeTransportPhaseUi(t, now);
  if (!phaseUi) {
    const elapsed = now - new Date(t.startedAt).getTime();
    const total   = new Date(t.arrivesAt).getTime() - new Date(t.startedAt).getTime();
    const pct     = total > 0 ? Math.min(100, Math.floor((elapsed / total) * 100)) : 100;
    return `
      <div class="lh-phase-wrap">
        <span class="lh-phase-label" style="color:var(--accent-gold);">LIEFERUNG</span>
        <div class="lh-at-bar"><div class="lh-at-fill" style="width:${String(pct)}%;"></div></div>
      </div>`;
  }

  const activeColorVar = phaseUi.segments.find((s) => s.isActive)?.colorVar ?? '--accent-gold';
  const segmentsHtml = phaseUi.segments.map((s) => `
    <div class="lh-phase-seg" style="flex-basis:${String(s.widthPct)}%;">
      <div class="lh-phase-seg-fill" style="width:${String(s.fillPct)}%;background:var(${s.colorVar});"></div>
    </div>`).join('');

  return `
    <div class="lh-phase-wrap">
      <span class="lh-phase-label" style="color:var(${activeColorVar});">${phaseUi.activeLabel}</span>
      <div class="lh-phase-bar">${segmentsHtml}</div>
    </div>`;
}

function renderActiveTransportsSection(): string {
  const active = transportData?.active ?? [];
  if (active.length === 0) return '';

  const maxParallel = transportData?.maxParallel ?? 1;
  const talentHint  = maxParallel >= 4 ? 'LOGISTIK III' : maxParallel >= 2 ? 'LOGISTIK II' : null;
  const slotLabel   = talentHint
    ? `${String(active.length)}&nbsp;/&nbsp;${String(maxParallel)}&nbsp;SLOTS&nbsp;&mdash;&nbsp;${talentHint}`
    : `${String(active.length)}&nbsp;/&nbsp;${String(maxParallel)}&nbsp;SLOTS`;

  const now = Date.now();
  const transportRows = active.map((t) => {
    const remaining     = Math.max(0, new Date(t.arrivesAt).getTime() - now);
    const toName        = esc(formatWarehouseName(t.toLabel));
    const hasBundle     = t.bundleItems !== undefined && t.bundleItems.length > 0;
    const itemLabel     = hasBundle ? 'MISCHLADUNG' : `${esc(formatItemKey(t.itemKey))} ${renderItemBadges(t.itemKey, 'lh-q-badge')}`;
    // Ziellager voll (NT-STORAGE-ENFORCE-01) — Ware bleibt sicher im Transport, wartet auf Platz.
    const isWaiting     = t.status === 'WAITING_FOR_SPACE';
    // Countdown sitzt rechtsbündig in derselben Zeile wie das Transportziel (NT-UI-LAYOUT-RERENDER-FIX-01,
    // Abschnitt B) — vorher eigene Zeile unterhalb der Zielzeile, dadurch wirkte die Karte aufgebläht.
    const timeHtml      = isWaiting ? '' : `<span class="lh-at-time" data-lh-transport="${esc(t.id)}">${fmtCountdown(remaining)}</span>`;
    const progressHtml  = isWaiting
      ? `<span class="lh-at-waiting">WARTET AUF LAGERPLATZ IM ZIELLAGER</span>`
      : `${renderTransportPhaseBar(t, now)}
         <button class="lh-transport-skip-btn" data-lh-transport-skip="${esc(t.id)}">${String(TRANSPORT_SKIP_COST_SCHEINE_DISPLAY)}&nbsp;SCHEINE&nbsp;-&nbsp;SOFORT</button>`;

    return `
      <div class="lh-at-row">
        <div class="lh-at-info">
          <span class="lh-at-item">${itemLabel}&nbsp;&#215;${String(t.quantity)}</span>
          <span class="lh-at-arrow">&#10140;</span>
          <span class="lh-at-dest">${toName}</span>
          ${timeHtml}
        </div>
        ${renderTransportContents(t)}
        <div class="lh-at-progress">
          ${progressHtml}
        </div>
      </div>`;
  }).join('');

  return `
    <div class="lh-at-section">
      <div class="lh-at-header">
        <span class="lh-at-title">AKTIVE TRANSPORTE</span>
        <span class="lh-at-slots">${slotLabel}</span>
      </div>
      ${transportRows}
    </div>`;
}

// ─── Render: Tab-Leiste ───────────────────────────────────────────────────────

function renderTabBar(): string {
  return `
    <div class="lh-tabs">
      <button class="lh-tab-btn${activeTab === 'lager' ? ' is-active' : ''}" data-lh-main-tab="lager">LAGER</button>
      <button class="lh-tab-btn${activeTab === 'garage' ? ' is-active' : ''}" data-lh-main-tab="garage">GARAGE</button>
      <button class="lh-tab-btn${activeTab === 'konsumierbare' ? ' is-active' : ''}" data-lh-main-tab="konsumierbare">TRICKKISTE</button>
    </div>`;
}

function renderPage(): string {
  if (propsData === null) {
    return `<section class="dash-content" style="padding:0;" id="lh-root"><p style="font-family:var(--font-mono);font-size:12px;color:var(--text-muted);text-align:center;padding:var(--space-8) 0;">LADE...</p></section>`;
  }

  const allProps  = allProperties();
  const total     = propsData.totalWarehouseSlots + propsData.totalHideoutSlots;
  const used      = propsData.totalUsedSlots;
  const pending   = pendingOrders.length;
  const inTransit = transportData?.active.length ?? 0;
  const canStart  = transportData?.canStart ?? true;

  const lagerContent = `
    ${pending > 0 || inTransit > 0 ? `
      <div class="lh-status-bar">
        ${pending > 0 ? `<span class="lh-status-item lh-status-item--delivery">&#9654; ${String(pending)} LIEFERUNG${pending > 1 ? 'EN' : ''}</span>` : ''}
        ${inTransit > 0 ? `<span class="lh-status-item lh-status-item--transit">&#10140; ${String(inTransit)} TRANSPORT${inTransit > 1 ? 'E' : ''}</span>` : ''}
        ${!canStart && inTransit > 0 ? `<span class="lh-status-item lh-status-item--warn">MAX TRANSPORTE AKTIV</span>` : ''}
      </div>` : ''}
    ${renderActiveTransportsSection()}
    ${renderActiveHehlerOrdersSection()}
    <div class="lh-list">
      ${allProps.length === 0
        ? `<div class="lh-no-props">
             <p class="lh-no-props-text">Kein Lager vorhanden.</p>
             <p class="lh-no-props-hint">Kaufe unter IMMOBILIEN &rarr; LAGER dein erstes Lager.</p>
           </div>`
        : allProps.map(renderPropertyCard).join('')}
    </div>`;

  const garageContent = `
    ${renderGarageSection()}
    ${renderVehicleInventoryCards()}`;

  const konsumierbareContent = renderKonsumierbareContent();

  const activeContent = activeTab === 'lager'
    ? lagerContent
    : activeTab === 'garage'
      ? garageContent
      : konsumierbareContent;

  return `
    <section class="dash-content" style="padding:0;" id="lh-root">
      <div class="lh-header">
        <div>
          <h2 class="lh-title">LAGERHAUS</h2>
          <p class="lh-subtitle">Bestandsübersicht aller Lager und Verstecke.</p>
        </div>
        ${total > 0 ? `
          <div class="lh-summary">
            <span class="lh-summary-label">GESAMT</span>
            <span class="lh-summary-value">${String(used)}&nbsp;/&nbsp;${String(total)}&nbsp;SLOTS</span>
          </div>` : ''}
      </div>

      ${renderTabBar()}

      ${activeContent}

      ${renderSendModal()}
      ${renderTransferModal()}
      ${renderUnloadModal()}
      ${renderSellModal()}
      ${renderHehlerModal()}
      ${renderWashModal()}
      ${renderConsumableUseModal()}
    </section>`;
}

// ─── Render: KONSUMIERBARE-Tab (NT-CONSUMABLE-01) ─────────────────────────────

const CONSUMABLE_CATEGORY_ORDER: readonly ConsumableInventoryEntry['category'][] = [
  'ZEIT', 'WIRTSCHAFT', 'SCHUTZ', 'RUF', 'SELTEN',
];
const CONSUMABLE_CATEGORY_LABELS: Record<ConsumableInventoryEntry['category'], string> = {
  ZEIT: 'ZEIT & BESCHLEUNIGUNG',
  WIRTSCHAFT: 'WIRTSCHAFT & GELD',
  SCHUTZ: 'SCHUTZ & SICHERHEIT',
  RUF: 'RUF & SOCIAL',
  SELTEN: 'SELTEN',
};
// Genre-übliche Rarity-Farbfolge (grau → grün → blau → lila), damit die Stufen auf den ersten
// Blick erkennbar sind — muss synchron mit stream.ts::RARITY_COLOR_VAR bleiben.
const CONSUMABLE_RARITY_VAR: Record<ConsumableInventoryEntry['rarity'], string> = {
  COMMON: '--text-muted',
  UNCOMMON: '--accent-green',
  RARE: '--accent-blue',
  EPIC: '--accent-purple',
};

function renderConsumableCard(c: ConsumableInventoryEntry): string {
  const capReached = c.dailyUsageCount >= c.dailyCap;
  return `
    <div class="lh-cons-card" style="border-left-color:var(${CONSUMABLE_RARITY_VAR[c.rarity]});">
      <div class="lh-cons-card-head">
        <span class="lh-cons-name">${esc(c.nameDE.toUpperCase())}</span>
        <span class="lh-cons-qty">${String(c.quantity)}x</span>
      </div>
      <p class="lh-cons-effect">${esc(c.effectDescriptionDE)}</p>
      <p class="lh-cons-desc">${esc(c.descriptionDE)}</p>
      <div class="lh-cons-card-footer">
        <span class="lh-cons-cap">HEUTE: ${String(c.dailyUsageCount)}/${String(c.dailyCap)}</span>
        <button class="btn lh-btn--sm" data-lh-cons-use="${esc(c.itemType)}" ${capReached ? 'disabled' : ''}>BENUTZEN</button>
      </div>
    </div>`;
}

function renderKonsumierbareContent(): string {
  if (consumables.length === 0) {
    return `
      <div class="lh-no-props">
        <p class="lh-no-props-text">Die Trickkiste ist leer.</p>
        <p class="lh-no-props-hint">Inhalt droppt aus Kisten, Challenges und Events.</p>
      </div>`;
  }

  const byCategory = new Map<ConsumableInventoryEntry['category'], ConsumableInventoryEntry[]>();
  for (const c of consumables) {
    const arr = byCategory.get(c.category) ?? [];
    arr.push(c);
    byCategory.set(c.category, arr);
  }

  return CONSUMABLE_CATEGORY_ORDER
    .filter((cat) => byCategory.has(cat))
    .map((cat) => `
      <div class="lh-cons-section">
        <div class="lh-cons-section-title">${CONSUMABLE_CATEGORY_LABELS[cat]}</div>
        ${(byCategory.get(cat) ?? []).map(renderConsumableCard).join('')}
      </div>`)
    .join('');
}

function renderConsumableUseModal(): string {
  if (!consumableUseModal) return '';
  const m = consumableUseModal;
  const def = consumables.find((c) => c.itemType === m.itemType);
  if (!def) return '';

  const needsTransportTarget = m.itemType === 'EXPRESS_LIEFERUNG';
  const needsPlantTarget = m.itemType === 'DUENGER_BEUTEL' || m.itemType === 'TURBO_DUENGER';
  const needsJobTarget = m.itemType === 'PARKPLATZ_ZETTEL';
  const needsItemKeyTarget = m.itemType === 'VISITENKARTE_RUECKSEITE';

  const plantOptions = growingPlants.length === 0
    ? '<option value="">– Keine wachsenden Pflanzen –</option>'
    : [
        '<option value="">– AUSWÄHLEN –</option>',
        ...growingPlants.map((p) =>
          `<option value="${esc(p.id)}" ${p.id === m.targetId ? 'selected' : ''}>${esc(formatItemKey(p.strainKey))}</option>`),
      ].join('');

  const activeTransports = (transportData?.active ?? []).filter((t) => t.status === 'IN_TRANSIT');
  const transportOptions = activeTransports.length === 0
    ? '<option value="">– Kein aktiver Transport –</option>'
    : [
        '<option value="">– AUSWÄHLEN –</option>',
        ...activeTransports.map((t) =>
          `<option value="${esc(t.id)}" ${t.id === m.targetId ? 'selected' : ''}>${esc(formatItemKey(t.itemKey))} &rarr; ${esc(t.toLabel)}</option>`),
      ].join('');

  const jobOptions = jobsInCooldown.length === 0
    ? '<option value="">– Kein Job im Cooldown –</option>'
    : [
        '<option value="">– AUSWÄHLEN –</option>',
        ...jobsInCooldown.map((j) =>
          `<option value="${esc(j.jobId)}" ${j.jobId === m.targetId ? 'selected' : ''}>${esc(j.jobName)}</option>`),
      ].join('');

  // VISITENKARTE_RUECKSEITE: dedupliziertes Angebot aus dem tatsächlichen Lagerbestand (Lager +
  // Verstecke) — die Karte fragt den Einkaufspreis von morgen ab, sinnvoll nur für Ware, die der
  // Spieler ohnehin schon führt (kein voller Markt-Katalog-Fetch nur für diese Auswahl).
  const ownedItemKeys = Array.from(new Set(
    [...(propsData?.warehouses ?? []), ...(propsData?.hideouts ?? [])]
      .flatMap((p) => p.inventoryItems)
      .map((i) => i.itemKey),
  )).sort((a, b) => a.localeCompare(b));
  const itemKeyOptions = ownedItemKeys.length === 0
    ? '<option value="">– Kein Item im Lager –</option>'
    : [
        '<option value="">– AUSWÄHLEN –</option>',
        ...ownedItemKeys.map((key) =>
          `<option value="${esc(key)}" ${key === m.targetId ? 'selected' : ''}>${esc(formatItemKey(key))}</option>`),
      ].join('');

  const canConfirm = !needsPlantTarget && !needsTransportTarget && !needsJobTarget && !needsItemKeyTarget
    ? true
    : Boolean(m.targetId);

  return `
    <div class="modal-backdrop" id="lh-cons-use-modal">
      <div class="modal modal--narrow">
        <div class="modal-header">
          <h2>${esc(def.nameDE.toUpperCase())}</h2>
          <p class="lh-cons-effect lh-cons-effect--modal">${esc(def.effectDescriptionDE)}</p>
          <p class="modal-subtitle">${esc(def.descriptionDE)}</p>
        </div>
        <div class="modal-body">
          ${needsPlantTarget ? `
          <div class="lh-modal-row">
            <label class="lh-modal-label">PFLANZE WÄHLEN</label>
            <select class="markt-select" id="lh-cons-target-select">${plantOptions}</select>
          </div>` : ''}
          ${needsTransportTarget ? `
          <div class="lh-modal-row">
            <label class="lh-modal-label">TRANSPORT WÄHLEN</label>
            <select class="markt-select" id="lh-cons-target-select">${transportOptions}</select>
          </div>` : ''}
          ${needsJobTarget ? `
          <div class="lh-modal-row">
            <label class="lh-modal-label">JOB WÄHLEN</label>
            <select class="markt-select" id="lh-cons-target-select">${jobOptions}</select>
          </div>` : ''}
          ${needsItemKeyTarget ? `
          <div class="lh-modal-row">
            <label class="lh-modal-label">WARE WÄHLEN</label>
            <select class="markt-select" id="lh-cons-target-select">${itemKeyOptions}</select>
          </div>` : ''}
          ${m.errorMsg ? `<p class="lh-modal-warn">${esc(m.errorMsg)}</p>` : ''}
        </div>
        <div class="modal-actions">
          <button class="btn" id="lh-confirm-cons-use" ${!canConfirm ? 'disabled' : ''}>BENUTZEN</button>
          <button class="btn" data-close-cons-use-modal>ABBRECHEN</button>
        </div>
      </div>
    </div>`;
}

// ─── Styles ───────────────────────────────────────────────────────────────────

function addStyles(): void {
  if (document.getElementById('lh-styles')) return;
  const s = document.createElement('style');
  s.id = 'lh-styles';
  s.textContent = `
    /* ── Seiten-Header ── */
    .lh-header { display:flex;align-items:flex-start;justify-content:space-between;flex-wrap:wrap;gap:var(--space-2);padding:var(--space-3) var(--space-4) var(--space-2);border-bottom:1px solid var(--border); }
    .lh-title  { font-family:var(--font-mono);font-size:var(--font-size-base);letter-spacing:.12em;color:var(--accent-gold);margin:0 0 4px;text-transform:uppercase; }
    @media(min-width:1024px){.lh-title{font-size:var(--font-size-xl)}}
    .lh-subtitle { font-family:var(--font-mono);font-size:var(--font-size-xs);color:var(--text-muted);margin:0;line-height:1.4; }
    .lh-summary { text-align:right; }
    .lh-summary-label { display:block;font-family:var(--font-mono);font-size:11px;color:var(--text-muted);letter-spacing:.10em; }
    .lh-summary-value { font-family:var(--font-mono);font-size:var(--font-size-sm);color:var(--text-primary); }

    /* ── Tab-Leiste ── */
    .lh-tabs { display:flex;gap:var(--space-2);flex-wrap:wrap;padding:var(--space-3) var(--space-4);border-bottom:1px solid var(--border); }
    .lh-tab-btn { font-family:var(--font-mono);font-size:var(--font-size-sm);letter-spacing:.06em;text-transform:uppercase;padding:var(--space-2) var(--space-4);background:transparent;border:1px solid var(--border);border-radius:var(--radius-btn);color:var(--text-muted);cursor:pointer;min-height:44px;transition:color .15s,border-color .15s; }
    .lh-tab-btn:hover { color:var(--accent-gold);border-color:color-mix(in srgb,var(--accent-gold) 50%,transparent); }
    .lh-tab-btn.is-active { color:var(--accent-gold);border-color:var(--accent-gold); }

    /* ── Status-Leiste ── */
    .lh-status-bar { display:flex;gap:var(--space-3);align-items:center;flex-wrap:wrap;padding:var(--space-2) var(--space-4);border-bottom:1px solid var(--border); }
    .lh-status-item { font-family:var(--font-mono);font-size:var(--font-size-xs);letter-spacing:.06em; }
    .lh-status-item--delivery { color:var(--accent-gold); }
    .lh-status-item--transit  { color:var(--accent-blue); }
    .lh-status-item--warn     { color:var(--accent-orange); }

    /* ── Karten-Liste (Einzelspalte, volle Breite) ── */
    .lh-list { display:flex;flex-direction:column;gap:0;padding:var(--space-3) var(--space-4); gap:var(--space-2); }

    /* ── Dropdown-Karte ── */
    .lh-card { border:1px solid var(--border);border-radius:var(--radius);background:var(--bg-elevated);overflow:hidden; }
    .lh-card--inactive { opacity:.55; }

    /* ── Toggle-Header (immer sichtbar) ── */
    .lh-card-toggle {
      display:flex;align-items:center;justify-content:space-between;gap:var(--space-3);
      padding:var(--space-3) var(--space-3);cursor:pointer;
      border-bottom:1px solid transparent;
      transition:background .15s;
      flex-wrap:wrap;
    }
    .lh-card-toggle:hover { background:color-mix(in srgb,var(--text-primary) 4%,var(--bg-elevated)); }
    .lh-card--expanded .lh-card-toggle { border-bottom-color:var(--border); }

    .lh-card-toggle-left { display:flex;align-items:baseline;gap:var(--space-2);flex-wrap:wrap;min-width:0; }
    .lh-card-name { font-family:var(--font-mono);font-size:var(--font-size-sm);color:var(--text-primary);letter-spacing:.06em;white-space:nowrap; }
    .lh-card-type-badge { font-family:var(--font-mono);font-size:11px;color:var(--text-muted);letter-spacing:.08em;border:1px solid var(--border);padding:1px 5px;border-radius:2px; }
    .lh-badge { font-family:var(--font-mono);font-size:11px;letter-spacing:.06em;text-transform:uppercase; }
    .lh-badge--inactive { color:var(--accent-red); }

    .lh-card-toggle-right { display:flex;align-items:center;gap:var(--space-3);flex:1;min-width:200px; }
    .lh-slot-row { display:flex;align-items:center;gap:var(--space-2);flex:1; }
    .lh-slot-track { flex:1;height:6px;background:var(--bg-surface);border:1px solid var(--border);border-radius:2px;overflow:hidden; }
    .lh-slot-fill  { height:100%;border-radius:2px;transition:width .3s; }
    .lh-slot-label { font-family:var(--font-mono);font-size:var(--font-size-xs);color:var(--text-muted);white-space:nowrap; }
    .lh-toggle-arrow { font-family:var(--font-mono);font-size:var(--font-size-xs);color:var(--text-muted);letter-spacing:.04em;white-space:nowrap;user-select:none; }
    .lh-card--expanded .lh-toggle-arrow { color:var(--accent-gold); }

    /* ── Aufgeklappter Inhalt ── */
    .lh-card-body { display:none;padding:var(--space-3); }
    .lh-card--expanded .lh-card-body { display:block; }

    /* ── Kategorie-Abschnitte ── */
    .lh-cat-section { margin-bottom:var(--space-3); }
    .lh-cat-section:last-child { margin-bottom:0; }
    .lh-cat-title {
      font-family:var(--font-mono);font-size:11px;color:var(--text-muted);
      letter-spacing:.10em;text-transform:uppercase;
      margin:0 0 var(--space-1);padding-bottom:3px;
      border-bottom:1px solid var(--border);
    }
    .lh-cat-title--out { color:var(--accent-blue); border-color:color-mix(in srgb,var(--accent-blue) 30%,var(--border)); }
    .lh-cat-title--in  { color:var(--accent-green); border-color:color-mix(in srgb,var(--accent-green) 30%,var(--border)); }
    .lh-cat-items { display:flex;flex-direction:column;gap:2px; }

    /* ── Inventar-Zeilen ── */
    .lh-inv-line { display:flex;align-items:center;gap:var(--space-2);padding:3px 0;flex-wrap:wrap; }
    .lh-inv-key  { font-family:var(--font-mono);font-size:var(--font-size-sm);color:var(--text-primary);flex:1; }
    .lh-inv-qty   { font-family:var(--font-mono);font-size:var(--font-size-sm);color:var(--accent-gold);white-space:nowrap;min-width:28px; }
    .lh-inv-slots { font-family:var(--font-mono);font-size:var(--font-size-xs);color:var(--text-muted);white-space:nowrap; }
    .lh-empty-inv { font-family:var(--font-mono);font-size:var(--font-size-xs);color:var(--text-muted);margin:0;font-style:italic; }

    /* ── Lieferungen ── */
    .lh-deliveries { margin-top:var(--space-2);margin-bottom:var(--space-2); }
    .lh-delivery { display:flex;justify-content:space-between;align-items:center;padding:2px 0; }
    .lh-delivery-label { font-family:var(--font-mono);font-size:var(--font-size-xs);color:var(--text-muted); }
    .lh-delivery-time  { font-family:var(--font-mono);font-size:var(--font-size-xs);color:var(--accent-green); }

    /* ── Transporte ── */
    .lh-transports { margin-top:var(--space-2);margin-bottom:var(--space-2); }
    .lh-transport { display:flex;justify-content:space-between;align-items:flex-start;gap:var(--space-2);padding:3px 0;flex-wrap:wrap; }
    .lh-transport-label {
      font-family:var(--font-mono);
      font-size:var(--font-size-sm);
      color:var(--text-muted);
      flex:1 1 260px;
      min-width:0;
      line-height:1.35;
      overflow-wrap:anywhere;
    }
    .lh-transport-time  {
      font-family:var(--font-mono);
      font-size:var(--font-size-sm);
      color:var(--accent-blue);
      white-space:nowrap;
      text-align:right;
      min-width:64px;
      line-height:1.35;
    }
    .lh-transport-details-btn {
      margin-top:4px;
      padding:0;
      border:none;
      background:transparent;
      color:var(--accent-green);
      font-family:var(--font-mono);
      font-size:11px;
      letter-spacing:.08em;
      cursor:pointer;
      text-align:left;
      width:100%;
      line-height:1.3;
    }
    .lh-transport-details {
      display:none;
      margin-top:4px;
      border:1px solid var(--border);
      padding:6px;
      border-radius:2px;
      background:var(--bg-surface);
      width:100%;
    }
    .lh-transport-details.is-open { display:block; }
    .lh-transport-detail-row { display:flex;justify-content:space-between;align-items:flex-start;gap:var(--space-2);padding:2px 0; }
    .lh-transport-detail-name { font-family:var(--font-mono);font-size:var(--font-size-sm);line-height:1.3;color:var(--text-primary);overflow-wrap:anywhere; }
    .lh-transport-detail-qty { font-family:var(--font-mono);font-size:var(--font-size-sm);line-height:1.3;color:var(--accent-gold);white-space:nowrap; }

    /* ── Meta ── */
    .lh-meta { display:flex;gap:var(--space-3);flex-wrap:wrap;padding-top:var(--space-2);border-top:1px solid var(--border);margin-top:var(--space-2); }
    .lh-meta-item { font-family:var(--font-mono);font-size:var(--font-size-xs);color:var(--text-muted); }

    /* ── Spitzname ── */
    .lh-nickname-row { display:flex;gap:var(--space-2);align-items:center;padding-bottom:var(--space-2);margin-bottom:var(--space-2);border-bottom:1px solid var(--border); }
    .lh-nickname-input { font-family:var(--font-mono);font-size:var(--font-size-xs);
      background:var(--bg-surface);border:1px solid var(--border);border-radius:var(--radius-sm);
      color:var(--text-primary);padding:4px 6px;flex:1;min-width:0; }

    /* ── Leer-Zustand ── */
    .lh-no-props { padding:var(--space-6);text-align:center; }
    .lh-no-props-text { font-family:var(--font-mono);font-size:var(--font-size-sm);color:var(--text-muted);margin:0 0 var(--space-2); }
    .lh-no-props-hint { font-family:var(--font-mono);font-size:var(--font-size-xs);color:var(--text-muted);opacity:.6;margin:0; }

    /* ── Konsumierbare (NT-CONSUMABLE-01) ── */
    .lh-cons-section { padding:var(--space-3) var(--space-4); }
    .lh-cons-section-title { font-family:var(--font-mono);font-size:var(--font-size-xs);letter-spacing:.08em;color:var(--text-muted);margin-bottom:var(--space-2); }
    .lh-cons-card { background:var(--bg-surface);border:1px solid var(--border);border-left:3px solid var(--border);border-radius:var(--radius-btn);padding:var(--space-3);margin-bottom:var(--space-2); }
    .lh-cons-card-head { display:flex;justify-content:space-between;align-items:center;gap:var(--space-2); }
    .lh-cons-name { font-family:var(--font-mono);font-size:var(--font-size-sm);letter-spacing:.04em;color:var(--text-primary); }
    .lh-cons-qty { font-family:var(--font-mono);font-size:var(--font-size-sm);color:var(--accent-gold); }
    .lh-cons-effect { font-family:var(--font-mono);font-size:var(--font-size-xs);color:var(--text-primary);line-height:1.4;margin:var(--space-1) 0 0; }
    .lh-cons-effect--modal { color:var(--accent-green);margin-top:var(--space-1); }
    .lh-cons-desc { font-family:var(--font-mono);font-size:var(--font-size-xs);font-style:italic;color:var(--text-italic);margin:2px 0 var(--space-2); }
    .lh-cons-card-footer { display:flex;justify-content:space-between;align-items:center;gap:var(--space-2);flex-wrap:wrap; }
    .lh-cons-cap { font-family:var(--font-mono);font-size:var(--font-size-xs);color:var(--text-muted); }
    .lh-btn--sm { font-family:var(--font-mono);font-size:var(--font-size-xs);letter-spacing:.06em;padding:var(--space-1) var(--space-3);min-height:36px; }

    /* ── Aktive Transporte Sektion ── */
    .lh-at-section { padding:var(--space-3) var(--space-4);border-bottom:1px solid var(--border);background:var(--bg-surface); }
    .lh-at-header { display:flex;align-items:baseline;justify-content:space-between;margin-bottom:var(--space-2); }
    .lh-at-title { font-family:var(--font-mono);font-size:var(--font-size-xs);letter-spacing:.12em;color:var(--accent-blue);text-transform:uppercase; }
    .lh-at-slots { font-family:var(--font-mono);font-size:var(--font-size-xs);color:var(--text-muted); }
    .lh-at-row { margin-bottom:var(--space-2); }
    .lh-at-info { display:flex;align-items:flex-start;gap:var(--space-2);flex-wrap:wrap;margin-bottom:4px; }
    .lh-at-item { font-family:var(--font-mono);font-size:var(--font-size-sm);line-height:1.35;color:var(--text-primary);overflow-wrap:anywhere; }
    .lh-at-arrow { font-family:var(--font-mono);font-size:var(--font-size-sm);line-height:1.35;color:var(--text-muted); }
    .lh-at-dest { font-family:var(--font-mono);font-size:var(--font-size-sm);line-height:1.35;color:var(--accent-blue);overflow-wrap:anywhere; }
    .lh-at-progress { display:flex;align-items:center;gap:var(--space-2);min-height:22px; }
    .lh-at-bar { flex:1;height:5px;background:var(--bg-base);border:1px solid var(--border);border-radius:2px;overflow:hidden; }
    .lh-at-fill { height:100%;background:var(--accent-blue);border-radius:2px;transition:width .3s; }
    /* Phasen-Balken (NT-TRANSPORT-BELADUNG-01): Verladung/Fahrt/Abladung als 3 proportionale
       Segmente, Text-Label immer sichtbar (Barrierefreiheit, nicht nur Farbe). */
    .lh-phase-wrap { flex:1;min-width:0;display:flex;flex-direction:column;gap:2px; }
    .lh-phase-label { font-family:var(--font-mono);font-size:var(--font-size-xs);letter-spacing:.08em;text-transform:uppercase; }
    .lh-phase-bar { display:flex;gap:2px;height:5px; }
    .lh-phase-seg { flex-grow:1;flex-shrink:1;flex-basis:0;min-width:8px;height:100%;background:var(--bg-base);border:1px solid var(--border);border-radius:2px;overflow:hidden; }
    .lh-phase-seg-fill { height:100%;border-radius:2px;transition:width .3s; }
    .lh-at-time { font-family:var(--font-mono);font-size:var(--font-size-sm);color:var(--accent-blue);white-space:nowrap;min-width:64px;text-align:right;line-height:1.35; }
    /* Countdown rechtsbündig in der Zielzeile statt eigener Zeile (NT-UI-LAYOUT-RERENDER-FIX-01, B) —
       überschreibt Größe/Breite nur für den Fall ".lh-at-time" innerhalb ".lh-at-info", die
       Hehler-Sektion (".lh-at-progress .lh-at-time") bleibt unverändert auf der Basisregel oben. */
    .lh-at-info .lh-at-time { font-size:var(--font-size-xs); min-width:0; margin-left:auto; }
    .lh-at-waiting { font-family:var(--font-mono);font-size:var(--font-size-xs);letter-spacing:.06em;color:var(--accent-red);text-transform:uppercase; }
    .lh-transport-skip-btn {
      background:transparent;color:var(--accent-gold);border:1px solid var(--accent-gold);
      padding:var(--space-1) var(--space-2);font-family:var(--font-mono);font-size:var(--font-size-xs);
      letter-spacing:.06em;cursor:pointer;border-radius:var(--radius);white-space:nowrap;
    }
    .lh-transport-skip-btn:hover:not(:disabled) { background:var(--accent-gold);color:var(--bg-base); }
    .lh-transport-skip-btn:disabled { opacity:.45;cursor:not-allowed; }

    /* ── Garage-Sektion ── */
    .lh-garage-section { padding:var(--space-3) var(--space-4);border-bottom:1px solid var(--border);background:var(--bg-surface); }
    .lh-garage-header { display:flex;align-items:baseline;justify-content:space-between;margin-bottom:var(--space-2); }
    .lh-garage-title { font-family:var(--font-mono);font-size:var(--font-size-xs);letter-spacing:.12em;color:var(--accent-blue);margin:0;text-transform:uppercase; }
    .lh-garage-slots-badge { font-family:var(--font-mono);font-size:var(--font-size-xs);color:var(--text-muted); }
    .lh-garage-empty { font-family:var(--font-mono);font-size:var(--font-size-xs);color:var(--text-muted);margin:0;font-style:italic; }
    .lh-garage-rows { display:flex;flex-direction:column;gap:4px; }
    .lh-garage-row { display:flex;align-items:center;gap:var(--space-2);padding:5px 0;border-bottom:1px solid var(--border);flex-wrap:wrap; }
    .lh-garage-row:last-child { border-bottom:none; }
    .lh-garage-row--free { opacity:.5; }
    .lh-garage-row-icon { font-size:10px;color:var(--accent-blue);flex-shrink:0; }
    .lh-garage-row--free .lh-garage-row-icon { color:var(--text-muted); }
    .lh-garage-row-name { font-family:var(--font-mono);font-size:var(--font-size-xs);color:var(--text-primary);flex:1;min-width:80px; }
    .lh-role-badge { font-family:var(--font-mono);font-size:10px;letter-spacing:.06em;padding:1px 5px;border-radius:2px;border:1px solid;flex-shrink:0; }
    .lh-role-badge--job       { color:var(--accent-gold);border-color:var(--accent-gold); }
    .lh-role-badge--dealer    { color:var(--accent-orange);border-color:var(--accent-orange); }
    .lh-role-badge--transport { color:var(--accent-blue);border-color:var(--accent-blue); }
    .lh-role-badge--smuggling { color:var(--accent-purple);border-color:var(--accent-purple); }
    .lh-role-badge--auftrag   { color:var(--accent-blue);border-color:var(--accent-blue); }
    .lh-role-badge--free      { color:var(--text-muted);border-color:var(--border); }
    .lh-role-badge--base      { color:var(--text-muted);border-color:var(--border);opacity:.6; }
    /* NT-VIERTEL-FIX-01 (V1): Heimat-Stellplatz je Fahrzeug */
    .lh-role-badge--homeless  { color:var(--accent-orange);border-color:var(--accent-orange); }
    .lh-garage-row-loc { font-family:var(--font-mono);font-size:var(--font-size-xs);color:var(--text-muted);letter-spacing:.04em; }
    .lh-role-btn { font-family:var(--font-mono);font-size:10px;letter-spacing:.06em;text-transform:uppercase;padding:2px 8px;border:1px solid var(--accent-gold);color:var(--accent-gold);background:transparent;border-radius:var(--radius-btn);cursor:pointer;flex-shrink:0; }
    .lh-role-btn:hover { background:color-mix(in srgb,var(--accent-gold) 12%,transparent); }
    .lh-role-btn--clear { border-color:var(--text-muted);color:var(--text-muted); }
    .lh-role-btn--clear:hover { background:color-mix(in srgb,var(--text-muted) 12%,transparent); }
    .lh-role-btn--sell { border-color:var(--accent-red);color:var(--accent-red); }
    .lh-role-btn--sell:hover { background:color-mix(in srgb,var(--accent-red) 12%,transparent); }
    .lh-garage-sell-confirm { font-family:var(--font-mono);font-size:var(--font-size-xs);color:var(--accent-red);flex-shrink:0; }

    /* ── Karten-Aktionen ── */
    .lh-card-actions { display:flex;gap:var(--space-2);flex-wrap:wrap;padding:var(--space-2) 0 var(--space-1); }
    .lh-card-actions--top { border-bottom:1px solid var(--border); padding-bottom:var(--space-2); margin-bottom:var(--space-2); }
    .lh-send-btn {
      font-family:var(--font-mono);font-size:var(--font-size-xs);letter-spacing:.08em;text-transform:uppercase;
      padding:var(--space-2) var(--space-4);background:var(--accent-green);color:var(--bg-base);
      border:1px solid var(--accent-green);
      border-radius:var(--radius-btn);cursor:pointer;touch-action:manipulation;min-height:36px;
    }
    .lh-send-btn:hover { background:transparent;color:var(--accent-green); }
    .lh-sell-btn {
      font-family:var(--font-mono);font-size:var(--font-size-xs);letter-spacing:.08em;text-transform:uppercase;
      padding:var(--space-2) var(--space-4);background:var(--accent-gold);color:var(--bg-base);
      border:1px solid var(--accent-gold);
      border-radius:var(--radius-btn);cursor:pointer;touch-action:manipulation;min-height:36px;
    }
    .lh-sell-btn:hover { background:transparent;color:var(--accent-gold); }
    .lh-hehler-btn {
      font-family:var(--font-mono);font-size:var(--font-size-xs);letter-spacing:.08em;text-transform:uppercase;
      padding:var(--space-2) var(--space-4);background:transparent;color:var(--accent-purple);
      border:1px solid var(--accent-purple);
      border-radius:var(--radius-btn);cursor:pointer;touch-action:manipulation;min-height:36px;
    }
    .lh-hehler-btn:hover { background:var(--accent-purple);color:var(--bg-base); }
    .lh-hehler-confirm-btn { border-color:var(--accent-purple);color:var(--accent-purple); }
    .lh-hehler-confirm-btn:hover:not(:disabled) { background:var(--accent-purple);color:var(--bg-base); }
    .lh-wash-link-btn {
      margin-top:4px;padding:0;border:none;background:transparent;color:var(--accent-blue);
      font-family:var(--font-mono);font-size:11px;letter-spacing:.06em;cursor:pointer;text-align:left;
      width:100%;line-height:1.3;text-transform:uppercase;
    }
    .lh-wash-link-btn:hover { text-decoration:underline; }

    /* ── Lager-Ausbau + Fahrzeugslot (NT-STORAGE-UPGRADE-01) ── */
    .lh-upgrade-section { display:flex;flex-direction:column;gap:6px;padding:var(--space-2) 0;border-top:1px solid var(--border);margin-top:var(--space-2); }
    .lh-upgrade-row, .lh-vslot-row { display:flex;align-items:center;gap:var(--space-2);flex-wrap:wrap; }
    .lh-upgrade-label, .lh-vslot-label { font-family:var(--font-mono);font-size:var(--font-size-xs);letter-spacing:.06em;color:var(--text-muted);text-transform:uppercase;flex:1;min-width:120px; }
    .lh-upgrade-time { font-family:var(--font-mono);font-size:var(--font-size-sm);color:var(--accent-blue);white-space:nowrap; }
    .lh-upgrade-btn, .lh-vslot-btn {
      font-family:var(--font-mono);font-size:var(--font-size-xs);letter-spacing:.06em;text-transform:uppercase;
      padding:var(--space-1) var(--space-3);background:transparent;color:var(--accent-blue);
      border:1px solid var(--accent-blue);border-radius:var(--radius-btn);cursor:pointer;white-space:nowrap;
    }
    .lh-upgrade-btn:hover:not(:disabled), .lh-vslot-btn:hover:not(:disabled) { background:var(--accent-blue);color:var(--bg-base); }
    .lh-upgrade-btn:disabled, .lh-vslot-btn:disabled { opacity:.45;cursor:not-allowed; }
    .lh-vslot-btn--unassign { border-color:var(--accent-red);color:var(--accent-red); }
    .lh-vslot-btn--unassign:hover:not(:disabled) { background:var(--accent-red);color:var(--bg-base); }

    /* ── VERKAUFEN-Modal ── */
    .lh-sell-price-info { display:flex;align-items:baseline;justify-content:space-between;padding:6px 0;margin-bottom:var(--space-2);border-bottom:1px solid var(--border); }
    .lh-sell-price-label { font-family:var(--font-mono);font-size:11px;color:var(--text-muted);letter-spacing:.06em; }
    .lh-sell-price-val   { font-family:var(--font-mono);font-size:var(--font-size-sm);color:var(--accent-gold);letter-spacing:.04em; }
    .lh-sell-qty-row { display:flex;align-items:center;gap:var(--space-2);margin-bottom:var(--space-2); }
    .lh-sell-qbtn { font-family:var(--font-mono);font-size:var(--font-size-xs);line-height:1;padding:4px 10px;border-radius:var(--radius-btn);border:1px solid;cursor:pointer;background:transparent;letter-spacing:.06em;text-transform:uppercase; }
    .lh-sell-qbtn--dec { border-color:var(--accent-red);color:var(--accent-red); }
    .lh-sell-qbtn--dec:hover { background:var(--accent-red);color:var(--bg-base); }
    .lh-sell-qbtn--inc { border-color:var(--accent-green);color:var(--accent-green); }
    .lh-sell-qbtn--inc:hover { background:var(--accent-green);color:var(--bg-base); }
    .lh-sell-qbtn--max { border-color:var(--accent-blue);color:var(--accent-blue); }
    .lh-sell-qbtn--max:hover { background:var(--accent-blue);color:var(--bg-base); }
    .lh-sell-qval { font-family:var(--font-mono);font-size:var(--font-size-base);color:var(--text-primary);min-width:36px;text-align:center; }
    .lh-sell-total { display:flex;align-items:baseline;justify-content:space-between;padding:8px 0;border-top:1px solid var(--border); }
    .lh-sell-total-label { font-family:var(--font-mono);font-size:11px;color:var(--text-muted);letter-spacing:.08em; }
    .lh-sell-total-val { font-family:var(--font-mono);font-size:var(--font-size-base);color:var(--accent-gold);font-weight:600;letter-spacing:.04em; }
    .lh-sell-confirm-btn { background:var(--accent-gold);color:var(--bg-base);border-color:var(--accent-gold); }
    .lh-sell-confirm-btn:hover:not(:disabled) { background:transparent;color:var(--accent-gold); }
    .lh-sell-confirm-btn:disabled { opacity:.4;cursor:not-allowed; }

    /* ── Transfer-Modal / VERSENDEN-Modal ── */
    .lh-modal-row { margin-bottom:var(--space-3); }
    .lh-modal-label { display:block;font-family:var(--font-mono);font-size:11px;color:var(--text-muted);letter-spacing:.08em;margin-bottom:6px; }
    .lh-modal-warn  { font-family:var(--font-mono);font-size:var(--font-size-xs);color:var(--accent-red);margin:0; }
    .lh-modal-hint  { font-family:var(--font-mono);font-size:var(--font-size-xs);color:var(--text-muted);margin:0; }

    /* ── VERSENDEN-Modal: Item-Liste ── */
    .lh-send-items {
      display:flex;
      flex-direction:column;
      gap:4px;
      padding:4px 4px 4px 0;
    }
    .lh-send-cat-group { display:flex;flex-direction:column;gap:4px; }
    .lh-send-cat-header { font-family:var(--font-mono);font-size:11px;letter-spacing:.08em;color:var(--accent-blue);text-transform:uppercase;margin:0;padding-bottom:2px;border-bottom:1px solid var(--border); }
    .lh-send-item { display:flex;align-items:center;gap:var(--space-2);padding:5px 0; }
    .lh-send-check { width:14px;height:14px;cursor:pointer;accent-color:var(--accent-green);flex-shrink:0; }
    .lh-send-item-label { font-family:var(--font-mono);font-size:var(--font-size-xs);color:var(--text-primary);flex:1;cursor:pointer;line-height:1.2;overflow-wrap:break-word; }
    .lh-send-item-avail { font-family:var(--font-mono);font-size:11px;color:var(--text-muted); }
    .lh-send-qty-ctrl { display:flex;align-items:center;gap:5px;flex-shrink:0;margin-right:4px; }
    .lh-send-qty-ctrl--disabled { opacity:.4;pointer-events:none; }
    .lh-send-qbtn { font-family:var(--font-mono);font-size:var(--font-size-xs);line-height:1;min-width:28px;height:28px;border-radius:var(--radius-btn);border:1px solid;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;padding:0 4px;background:transparent; }
    .lh-send-qbtn--dec { border-color:var(--accent-red);color:var(--accent-red); }
    .lh-send-qbtn--dec:hover:not(:disabled) { background:var(--accent-red);color:var(--bg-base); }
    .lh-send-qbtn--inc { border-color:var(--accent-green);color:var(--accent-green); }
    .lh-send-qbtn--inc:hover:not(:disabled) { background:var(--accent-green);color:var(--bg-base); }
    .lh-send-qbtn--max { border-color:var(--accent-gold);color:var(--accent-gold);min-width:40px; }
    .lh-send-qbtn--max:hover:not(:disabled) { background:var(--accent-gold);color:var(--bg-base); }
    .lh-send-qbtn:disabled { opacity:.4;cursor:not-allowed; }
    .lh-send-qty-input { font-family:var(--font-mono);font-size:var(--font-size-xs);color:var(--text-primary);width:46px;text-align:center;background:var(--bg-base);border:1px solid var(--border);border-radius:var(--radius);padding:0 4px;height:28px;line-height:1;-moz-appearance:textfield; }
    .lh-send-qty-input:disabled { opacity:.4; }
    .lh-send-qty-input::-webkit-inner-spin-button,.lh-send-qty-input::-webkit-outer-spin-button { -webkit-appearance:none; }

    /* ── Fahrzeug-Inventar-Sektion ── */
    .lh-vehicle-inv-section { padding:var(--space-3) var(--space-4);border-bottom:1px solid var(--border); }
    .lh-section-title { font-family:var(--font-mono);font-size:var(--font-size-xs);letter-spacing:.12em;color:var(--accent-blue);text-transform:uppercase;margin-bottom:var(--space-2); }
    .lh-vehicle-inv-card { border:1px solid var(--border);border-radius:var(--radius);background:var(--bg-elevated);padding:var(--space-3);margin-bottom:var(--space-2); }
    .lh-vehicle-inv-card:last-child { margin-bottom:0; }
    .lh-vehicle-inv-header { display:flex;flex-direction:column;gap:var(--space-1);margin-bottom:var(--space-2); }
    .lh-vehicle-inv-title-row { display:flex;align-items:baseline;gap:var(--space-2);flex-wrap:wrap; }
    .lh-badge--warn { color:var(--accent-orange); }
    .lh-badge--import { color:var(--accent-orange);border:1px solid var(--accent-orange);padding:0 3px;border-radius:2px;font-size:10px; }
    .lh-q-badge { font-size:var(--font-size-xs);letter-spacing:0.05em;padding:1px 6px;border-radius:var(--radius-sm);border:1px solid;display:inline-block;text-transform:uppercase; }
    .lh-vehicle-inv-items { display:flex;flex-direction:column;gap:2px;margin-bottom:var(--space-2); }

    /* ── EINLAGERN-Button ── */
    .lh-unload-btn {
      font-family:var(--font-mono);font-size:var(--font-size-xs);letter-spacing:.08em;text-transform:uppercase;
      padding:var(--space-2) var(--space-4);background:var(--accent-blue);color:var(--bg-base);
      border:1px solid var(--accent-blue);
      border-radius:var(--radius-btn);cursor:pointer;touch-action:manipulation;min-height:36px;
    }
    .lh-unload-btn:hover { background:transparent;color:var(--accent-blue); }

    /* ── EINLAGERN-Modal: Confirm-Button ── */
    .lh-unload-confirm-btn {
      background:var(--accent-blue);color:var(--bg-base);border-color:var(--accent-blue);
    }
    .lh-unload-confirm-btn:hover:not(:disabled) { background:transparent;color:var(--accent-blue); }
    .lh-unload-confirm-btn:disabled { opacity:.4;cursor:not-allowed; }

    /* ── EINLAGERN-Modal: Item-Zeilen mit Qty-Controls ── */
    .lh-unload-item { display:flex;flex-direction:column;gap:6px;padding:6px 0;border-bottom:1px solid var(--border); }
    .lh-unload-item:last-child { border-bottom:none; }
    .lh-unload-item-top { display:flex;align-items:center;gap:var(--space-2); }
    .lh-unload-qty-ctrl { display:flex;align-items:center;gap:5px;padding-left:22px; }
    .lh-unload-qty-ctrl--disabled { opacity:.35;pointer-events:none; }
    .lh-unload-qbtn { font-family:var(--font-mono);font-size:var(--font-size-xs);line-height:1;min-width:28px;height:28px;border-radius:var(--radius-btn);border:1px solid;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;padding:0 4px;background:transparent; }
    .lh-unload-qbtn--dec { border-color:var(--accent-red);color:var(--accent-red); }
    .lh-unload-qbtn--dec:hover:not(:disabled) { background:var(--accent-red);color:var(--bg-base); }
    .lh-unload-qbtn--inc { border-color:var(--accent-green);color:var(--accent-green); }
    .lh-unload-qbtn--inc:hover:not(:disabled) { background:var(--accent-green);color:var(--bg-base); }
    .lh-unload-qbtn--max { border-color:var(--accent-gold);color:var(--accent-gold);min-width:40px; }
    .lh-unload-qbtn--max:hover:not(:disabled) { background:var(--accent-gold);color:var(--bg-base); }
    .lh-unload-qval { font-family:var(--font-mono);font-size:var(--font-size-sm);color:var(--text-primary);min-width:32px;text-align:center; }

    /* ── EINLAGERN-Modal: Überfüllungs-Hinweis ── */
    .lh-unload-overflow-hint {
      font-family:var(--font-mono);font-size:11px;color:var(--accent-orange);
      border:1px solid color-mix(in srgb,var(--accent-orange) 40%,var(--border));
      border-radius:var(--radius);padding:var(--space-2);margin-bottom:var(--space-3);
      letter-spacing:.04em;
    }

    @media (max-width: 640px) {
      .lh-send-items { padding-right:2px; }
      .lh-send-item {
        display:grid;
        grid-template-columns:14px 1fr;
        grid-template-areas:
          'check label'
          '. qty';
        row-gap:4px;
        column-gap:var(--space-2);
        align-items:start;
      }
      .lh-send-check { grid-area:check; margin-top:2px; }
      .lh-send-item-label { grid-area:label; line-height:1.3; }
      .lh-send-qty-ctrl {
        grid-area:qty;
        width:100%;
        margin-right:0;
      }
      .lh-send-qbtn { min-width:28px;height:28px; }
      .lh-transport { flex-direction:column;align-items:stretch; }
      .lh-transport-time { align-self:flex-end; }
      .lh-at-progress { flex-wrap:wrap; }
      .lh-at-bar { min-width:100%; }
      .lh-phase-wrap { min-width:100%; }
      .lh-at-time { min-width:auto; }
      .lh-transport-skip-btn { min-height:var(--touch-target-min);width:100%; }
    }
  `;
  document.head.appendChild(s);
}

// ─── Event-Handling ───────────────────────────────────────────────────────────

function rerender(): void {
  const root = document.getElementById('lh-root');
  if (!root) return;
  const tmp = document.createElement('div');
  tmp.innerHTML = renderPage();
  const newSection = tmp.firstElementChild;
  if (newSection) root.replaceWith(newSection);
  attachListeners();
}

function attachListeners(): void {
  const root = document.getElementById('lh-root');
  if (!root) return;

  root.addEventListener('click', (e) => {
    const target = e.target as Element;

    // Tab-Wechsel
    const mainTabBtn = target.closest<HTMLButtonElement>('[data-lh-main-tab]');
    if (mainTabBtn) {
      activeTab = mainTabBtn.dataset.lhMainTab as 'lager' | 'garage';
      rerender();
      return;
    }

    // Karte auf-/zuklappen
    const toggleEl = target.closest<HTMLElement>('[data-lh-toggle]');
    if (toggleEl && !target.closest('[data-lh-move]')) {
      const cardId = toggleEl.dataset.lhToggle ?? '';
      if (expandedCards.has(cardId)) {
        expandedCards.delete(cardId);
      } else {
        expandedCards.add(cardId);
      }
      rerender();
      return;
    }

    // Spitzname speichern
    const nickBtn = target.closest<HTMLButtonElement>('[data-lh-save-nickname]');
    if (nickBtn) {
      const propertyId = nickBtn.dataset.lhSaveNickname ?? '';
      const input = root.querySelector<HTMLInputElement>(`[data-lh-nickname-input="${CSS.escape(propertyId)}"]`);
      if (input) void handleSaveNickname(propertyId, input.value);
      return;
    }

    // Fahrzeug-Karte auf-/zuklappen
    const vehicleToggleEl = target.closest<HTMLElement>('[data-lh-vehicle-toggle]');
    if (vehicleToggleEl) {
      const vid = vehicleToggleEl.dataset.lhVehicleToggle ?? '';
      if (expandedVehicleCards.has(vid)) {
        expandedVehicleCards.delete(vid);
      } else {
        expandedVehicleCards.add(vid);
      }
      rerender();
      return;
    }

    // VERSENDEN-Modal öffnen
    const sendBtn = target.closest<HTMLButtonElement>('[data-lh-send]');
    if (sendBtn && !target.closest('[data-lh-toggle]')) {
      const fromId = sendBtn.dataset.lhSend ?? '';
      const fromProp = allProperties().find((p) => p.id === fromId);
      if (fromProp) {
        // NT-POLISH-01 (P2): Fahrzeug und Zielort starten leer (Platzhalter) — der Spieler wählt aktiv.
        sendModal = {
          fromPropertyId: fromId,
          vehicleKey:     '',
          items:          fromProp.inventoryItems
            .filter((i) => i.itemType !== 'VEHICLE' && i.quantity > 0)
            .map((i) => {
              const isPack = isPackItem(i.itemKey);
              const displayAvail = isPack ? packCount(i.itemKey, i.quantity) : i.quantity;
              const base = { itemKey: i.itemKey, itemType: i.itemType, available: displayAvail, selected: false as const, qty: 1 };
              return isPack ? { ...base, sheetsAvailable: i.quantity } : base;
            })
            // Alphabetisch nach Anzeigename sortiert — stabile Reihenfolge statt
            // API-Insertion-Order, damit Spieler Items wiederfinden ohne hin- und
            // herzuscrollen (Bug-Report: Reihenfolge sprang zwischen Renders).
            .sort((a, b) => formatItemKey(a.itemKey).localeCompare(formatItemKey(b.itemKey), 'de')),
          toPropertyId:   '',
          errorMsg:       null,
        };
        sendDurationPreviewText = ''; // Vorschau eines evtl. vorherigen Modals nicht übernehmen
        rerender();
      }
      return;
    }

    // Transport-Details ein-/ausklappen
    const transportDetailsBtn = target.closest<HTMLElement>('[data-lh-transport-details]');
    if (transportDetailsBtn) {
      const id = transportDetailsBtn.dataset.lhTransportDetails ?? '';
      if (expandedTransportDetails.has(id)) expandedTransportDetails.delete(id);
      else expandedTransportDetails.add(id);
      rerender();
      return;
    }

    // Transport sofort abschließen (Scheine-Skip)
    const transportSkipBtn = target.closest<HTMLButtonElement>('[data-lh-transport-skip]');
    if (transportSkipBtn?.dataset.lhTransportSkip) {
      void handleSkipTransport(transportSkipBtn.dataset.lhTransportSkip);
      return;
    }

    // VERSENDEN-Modal schließen
    if (target.closest('[data-close-send-modal]') || target.id === 'lh-send-modal') {
      sendModal = null;
      rerender();
      return;
    }

    // VERSENDEN bestätigen
    if (target.id === 'lh-confirm-send' && sendModal) {
      // Getippte Mengen aus Inputs in den State übernehmen und klemmen
      for (const item of sendModal.items) {
        if (!item.selected) continue;
        const qtyInput = document.querySelector<HTMLInputElement>(`[data-lh-qty-input="${item.itemKey}"]`);
        if (qtyInput) {
          const raw = parseInt(qtyInput.value, 10);
          item.qty = isNaN(raw) ? 1 : Math.max(1, Math.min(item.available, raw));
        }
      }
      const vehicleSel = document.getElementById('lh-send-vehicle') as HTMLSelectElement | null;
      const targetSel  = document.getElementById('lh-send-target')  as HTMLSelectElement | null;
      if (vehicleSel) sendModal.vehicleKey   = vehicleSel.value;
      if (targetSel)  sendModal.toPropertyId = targetSel.value;
      void handleStartSend();
      return;
    }

    // Modal öffnen: VERSCHIEBEN-Button
    const moveBtn = target.closest<HTMLElement>('[data-lh-move]');
    if (moveBtn) {
      const fromId  = moveBtn.dataset.lhMove ?? '';
      const itemKey = moveBtn.dataset.lhItem ?? '';
      const itemType = moveBtn.dataset.lhType ?? '';
      const maxQty  = parseInt(moveBtn.dataset.lhQty ?? '1', 10);
      const allProps = allProperties();
      const toDefault = allProps.find((p) => p.id !== fromId)?.id ?? '';
      const fromProp = allProps.find((p) => p.id === fromId);
      const firstVehicle = fromProp?.inventoryItems.find((i) => i.itemType === 'VEHICLE')?.itemKey ?? '';
      transferModal = { fromPropertyId: fromId, itemKey, itemType, availableQty: maxQty, qty: 1, toPropertyId: toDefault, vehicleKey: firstVehicle };
      rerender();
      return;
    }

    // Modal schließen
    if (target.closest('[data-close-lh-modal]') || target.id === 'lh-transfer-modal') {
      transferModal = null;
      rerender();
      return;
    }

    // Menge anpassen (Transfer-Modal)
    const qtyBtn = target.closest<HTMLButtonElement>('[data-lh-qty]');
    if (qtyBtn && transferModal) {
      const delta = parseInt(qtyBtn.dataset.lhQty ?? '0', 10);
      transferModal.qty = Math.max(1, Math.min(transferModal.availableQty, transferModal.qty + delta));
      const display = document.getElementById('lh-qty-display');
      if (display) display.textContent = String(transferModal.qty);
      return;
    }

    // VERSENDEN-Modal: Qty +
    const incBtn = target.closest<HTMLElement>('[data-lh-item-inc]');
    if (incBtn && sendModal) {
      const key = incBtn.dataset.lhItemInc ?? '';
      const item = sendModal.items.find((i) => i.itemKey === key);
      if (item?.selected) {
        item.qty = Math.min(item.available, item.qty + 1);
        const input = document.querySelector<HTMLInputElement>(`[data-lh-qty-input="${key}"]`);
        if (input) input.value = String(item.qty);
        scheduleSendDurationPreview();
      }
      return;
    }

    // VERSENDEN-Modal: Qty -
    const decBtn = target.closest<HTMLElement>('[data-lh-item-dec]');
    if (decBtn && sendModal) {
      const key = decBtn.dataset.lhItemDec ?? '';
      const item = sendModal.items.find((i) => i.itemKey === key);
      if (item?.selected) {
        item.qty = Math.max(1, item.qty - 1);
        const input = document.querySelector<HTMLInputElement>(`[data-lh-qty-input="${key}"]`);
        if (input) input.value = String(item.qty);
        scheduleSendDurationPreview();
      }
      return;
    }

    // VERSENDEN-Modal: Qty MAX
    const maxSendBtn = target.closest<HTMLElement>('[data-lh-item-max]');
    if (maxSendBtn && sendModal) {
      const key = maxSendBtn.dataset.lhItemMax ?? '';
      const item = sendModal.items.find((i) => i.itemKey === key);
      if (item?.selected) {
        item.qty = item.available;
        const input = document.querySelector<HTMLInputElement>(`[data-lh-qty-input="${key}"]`);
        if (input) input.value = String(item.qty);
        scheduleSendDurationPreview();
      }
      return;
    }

    // Transport bestätigen
    if (target.id === 'lh-confirm-transfer' && transferModal) {
      const vehicleSel = document.getElementById('lh-vehicle-select') as HTMLSelectElement | null;
      const targetSel  = document.getElementById('lh-target-select')  as HTMLSelectElement | null;
      if (vehicleSel) transferModal.vehicleKey   = vehicleSel.value;
      if (targetSel)  transferModal.toPropertyId = targetSel.value;
      void handleStartTransport();
      return;
    }

    // VERKAUFEN-Modal öffnen
    const sellBtn = target.closest<HTMLButtonElement>('[data-lh-sell]');
    if (sellBtn && !target.closest('[data-lh-toggle]')) {
      const propertyId   = sellBtn.dataset.lhSell ?? '';
      const propertyName = sellBtn.dataset.lhSellName ?? '';
      void openSellModal(propertyId, propertyName);
      return;
    }

    // VERKAUFEN-Modal schließen
    if (target.closest('[data-close-sell-modal]') || target.id === 'lh-sell-modal') {
      sellModal = null;
      rerender();
      return;
    }

    // VERKAUFEN bestätigen
    if (target.id === 'lh-confirm-sell' && sellModal) {
      void handleSell();
      return;
    }

    // KONSUMIERBARE: Benutzen-Modal öffnen
    const consUseBtn = target.closest<HTMLButtonElement>('[data-lh-cons-use]');
    if (consUseBtn) {
      consumableUseModal = { itemType: consUseBtn.dataset.lhConsUse ?? '', targetId: null, errorMsg: null };
      rerender();
      return;
    }

    // KONSUMIERBARE-Modal schließen
    if (target.closest('[data-close-cons-use-modal]') || target.id === 'lh-cons-use-modal') {
      consumableUseModal = null;
      rerender();
      return;
    }

    // KONSUMIERBARE bestätigen
    if (target.id === 'lh-confirm-cons-use' && consumableUseModal) {
      void handleUseConsumable();
      return;
    }

    // VERKAUFEN-Modal: Qty-Buttons
    if (target.id === 'lh-sell-qty-dec' && sellModal) {
      sellModal.qty = Math.max(1, sellModal.qty - 1);
      updateSellModalQty();
      return;
    }
    if (target.id === 'lh-sell-qty-inc' && sellModal) {
      const selectedKey = sellModal.selectedItemKey;
      const maxQ = sellModal.items.find((i) => i.itemKey === selectedKey)?.quantity ?? 1;
      sellModal.qty = Math.min(maxQ, sellModal.qty + 1);
      updateSellModalQty();
      return;
    }
    if (target.id === 'lh-sell-qty-max' && sellModal) {
      const selectedKey = sellModal.selectedItemKey;
      const maxQ = sellModal.items.find((i) => i.itemKey === selectedKey)?.quantity ?? 1;
      sellModal.qty = maxQ;
      updateSellModalQty();
      return;
    }

    // AN HEHLER-Modal öffnen
    const hehlerBtn = target.closest<HTMLButtonElement>('[data-lh-hehler]');
    if (hehlerBtn && !target.closest('[data-lh-toggle]')) {
      const propertyId   = hehlerBtn.dataset.lhHehler ?? '';
      const propertyName = hehlerBtn.dataset.lhHehlerName ?? '';
      const prop = allProperties().find((p) => p.id === propertyId);
      hehlerModal = {
        propertyId,
        propertyName,
        items: (prop?.inventoryItems ?? [])
          .filter((i) => HEHLER_ITEM_KEYS.has(i.itemKey) && i.quantity > 0)
          .map((i) => ({ itemKey: i.itemKey, itemType: i.itemType, available: i.quantity, selected: false, qty: 1 }))
          .sort((a, b) => formatItemKey(a.itemKey).localeCompare(formatItemKey(b.itemKey), 'de')),
        errorMsg: null,
      };
      rerender();
      return;
    }

    // AN HEHLER-Modal schließen
    if (target.closest('[data-close-hehler-modal]') || target.id === 'lh-hehler-modal') {
      hehlerModal = null;
      rerender();
      return;
    }

    // AN HEHLER: Item-Checkbox
    const hehlerCheckEl = target.closest<HTMLInputElement>('[data-lh-hehler-check]');
    if (hehlerCheckEl && hehlerModal) {
      const key = hehlerCheckEl.dataset.lhHehlerCheck ?? '';
      const item = hehlerModal.items.find((i) => i.itemKey === key);
      if (item) { item.selected = !item.selected; rerender(); }
      return;
    }

    // AN HEHLER: Qty +/-/MAX
    const hehlerIncBtn = target.closest<HTMLElement>('[data-lh-hehler-inc]');
    if (hehlerIncBtn && hehlerModal) {
      const key = hehlerIncBtn.dataset.lhHehlerInc ?? '';
      const item = hehlerModal.items.find((i) => i.itemKey === key);
      if (item?.selected) {
        item.qty = Math.min(item.available, item.qty + 1);
        const input = document.querySelector<HTMLInputElement>(`[data-lh-hehler-qty-input="${key}"]`);
        if (input) input.value = String(item.qty);
      }
      return;
    }
    const hehlerDecBtn = target.closest<HTMLElement>('[data-lh-hehler-dec]');
    if (hehlerDecBtn && hehlerModal) {
      const key = hehlerDecBtn.dataset.lhHehlerDec ?? '';
      const item = hehlerModal.items.find((i) => i.itemKey === key);
      if (item?.selected) {
        item.qty = Math.max(1, item.qty - 1);
        const input = document.querySelector<HTMLInputElement>(`[data-lh-hehler-qty-input="${key}"]`);
        if (input) input.value = String(item.qty);
      }
      return;
    }
    const hehlerMaxBtn = target.closest<HTMLElement>('[data-lh-hehler-max]');
    if (hehlerMaxBtn && hehlerModal) {
      const key = hehlerMaxBtn.dataset.lhHehlerMax ?? '';
      const item = hehlerModal.items.find((i) => i.itemKey === key);
      if (item?.selected) {
        item.qty = item.available;
        const input = document.querySelector<HTMLInputElement>(`[data-lh-hehler-qty-input="${key}"]`);
        if (input) input.value = String(item.qty);
      }
      return;
    }

    // AN HEHLER bestätigen
    if (target.id === 'lh-confirm-hehler' && hehlerModal) {
      for (const item of hehlerModal.items) {
        if (!item.selected) continue;
        const qtyInput = document.querySelector<HTMLInputElement>(`[data-lh-hehler-qty-input="${item.itemKey}"]`);
        if (qtyInput) {
          const raw = parseInt(qtyInput.value, 10);
          item.qty = isNaN(raw) ? 1 : Math.max(1, Math.min(item.available, raw));
        }
      }
      void handleSubmitHehler();
      return;
    }

    // Hehler-Auftrag sofort abschließen (Scheine-Skip)
    const hehlerSkipBtn = target.closest<HTMLButtonElement>('[data-lh-hehler-skip]');
    if (hehlerSkipBtn?.dataset.lhHehlerSkip) {
      void handleSkipHehler(hehlerSkipBtn.dataset.lhHehlerSkip);
      return;
    }

    // WASCHEN-Modal öffnen (Bargeld-Bündel-Sonderweg, aus dem AN HEHLER-Modal heraus)
    const openWashBtn = target.closest<HTMLButtonElement>('[data-lh-open-wash]');
    if (openWashBtn) {
      const propertyId = openWashBtn.dataset.lhOpenWash ?? '';
      const available   = parseInt(openWashBtn.dataset.lhWashAvail ?? '1', 10);
      void openWashModal(propertyId, available);
      return;
    }

    // WASCHEN-Modal schließen
    if (target.closest('[data-close-wash-modal]') || target.id === 'lh-wash-modal') {
      washModal = null;
      rerender();
      return;
    }

    // WASCHEN: Menge
    if (target.id === 'lh-wash-qty-dec' && washModal) {
      washModal.qty = Math.max(1, washModal.qty - 1);
      const el = document.getElementById('lh-wash-qty-display');
      if (el) el.textContent = String(washModal.qty);
      return;
    }
    if (target.id === 'lh-wash-qty-inc' && washModal) {
      washModal.qty = Math.min(washModal.available, washModal.qty + 1);
      const el = document.getElementById('lh-wash-qty-display');
      if (el) el.textContent = String(washModal.qty);
      return;
    }
    if (target.id === 'lh-wash-qty-max' && washModal) {
      washModal.qty = washModal.available;
      const el = document.getElementById('lh-wash-qty-display');
      if (el) el.textContent = String(washModal.qty);
      return;
    }

    // WASCHEN bestätigen
    if (target.id === 'lh-confirm-wash' && washModal) {
      const businessSel = document.getElementById('lh-wash-business') as HTMLSelectElement | null;
      if (businessSel) washModal.selectedBusinessId = businessSel.value;
      void handleWashBargeldBuendel();
      return;
    }

    // Lager-Ausbau starten
    const upgradeBtn = target.closest<HTMLButtonElement>('[data-lh-upgrade-start]');
    if (upgradeBtn?.dataset.lhUpgradeStart) {
      void handleStartStorageUpgrade(upgradeBtn.dataset.lhUpgradeStart);
      return;
    }

    // Fahrzeugslot kaufen
    const vslotBuyBtn = target.closest<HTMLButtonElement>('[data-lh-vslot-buy]');
    if (vslotBuyBtn?.dataset.lhVslotBuy) {
      void handleBuyVehicleSlot(vslotBuyBtn.dataset.lhVslotBuy);
      return;
    }

    // Fahrzeug dem Fahrzeugslot zuweisen
    const vslotAssignBtn = target.closest<HTMLButtonElement>('[data-lh-vslot-assign]');
    if (vslotAssignBtn?.dataset.lhVslotAssign) {
      const propertyId = vslotAssignBtn.dataset.lhVslotAssign;
      const select = document.querySelector<HTMLSelectElement>(`[data-lh-vslot-select="${CSS.escape(propertyId)}"]`);
      const vehicleId = select?.value ?? '';
      if (vehicleId) void handleAssignVehicleSlot(propertyId, vehicleId);
      return;
    }

    // Fahrzeug vom Fahrzeugslot abmelden (F10-a)
    const vslotUnassignBtn = target.closest<HTMLButtonElement>('[data-lh-vslot-unassign]');
    if (vslotUnassignBtn?.dataset.lhVslotUnassign) {
      void handleUnassignVehicleSlot(vslotUnassignBtn.dataset.lhVslotUnassign);
      return;
    }

    // Lager für Pfand-Lager dedizieren (NT-PFAND-LAGER-LOOPB-01)
    const dedicateBtn = target.closest<HTMLButtonElement>('[data-lh-dedicate]');
    if (dedicateBtn?.dataset.lhDedicate) {
      void handleDedicateWarehouse(dedicateBtn.dataset.lhDedicate);
      return;
    }

    // Lager für die Werkstatt dedizieren (NT-WERKSTATT-PUFFER-REAL-INVENTORY-01)
    const dedicateWerkstattBtn = target.closest<HTMLButtonElement>('[data-lh-dedicate-werkstatt]');
    if (dedicateWerkstattBtn?.dataset.lhDedicateWerkstatt) {
      void handleDedicateWerkstattWarehouse(dedicateWerkstattBtn.dataset.lhDedicateWerkstatt);
      return;
    }

    // Dedizierung aufheben
    const undedicateBtn = target.closest<HTMLButtonElement>('[data-lh-undedicate]');
    if (undedicateBtn?.dataset.lhUndedicate) {
      void handleUndedicateWarehouse(undedicateBtn.dataset.lhUndedicate);
      return;
    }

    // EINLAGERN-Modal öffnen (Fahrzeug → Lager)
    const unloadBtn = target.closest<HTMLButtonElement>('[data-lh-unload]');
    if (unloadBtn) {
      const vehicleId = unloadBtn.dataset.lhUnload ?? '';
      const vehicle   = vehicleInventories.find((v) => v.vehicleId === vehicleId);
      if (vehicle) {
        const firstTarget = allProperties().find((p) => p.isActive);
        const grouped = new Map<string, { totalQty: number; slotCount: number; slots: { slotIndex: number; quantity: number }[] }>();
        for (const s of vehicle.slots) {
          if (s.itemKey === null) continue;
          const qty = s.quantity ?? 1;
          const entry = grouped.get(s.itemKey) ?? { totalQty: 0, slotCount: 0, slots: [] };
          entry.totalQty += qty;
          entry.slotCount += 1;
          entry.slots.push({ slotIndex: s.slotIndex, quantity: qty });
          grouped.set(s.itemKey, entry);
        }
        unloadModal = {
          vehicleId:     vehicle.vehicleId,
          vehicleLabel:  vehicle.label,
          capacity:      vehicle.capacity,
          occupiedSlots: vehicle.occupiedSlots,
          items: Array.from(grouped.entries()).map(([key, g]) => ({
            itemKey:     key,
            totalQty:    g.totalQty,
            transferQty: g.totalQty,
            slotCount:   g.slotCount,
            slots:       g.slots,
            selected:    false,
          })),
          toPropertyId:  firstTarget?.id ?? '',
          errorMsg:      null,
        };
        rerender();
      }
      return;
    }

    // EINLAGERN-Modal schließen
    if (target.closest('[data-close-unload-modal]') || target.id === 'lh-unload-modal') {
      unloadModal = null;
      rerender();
      return;
    }

    // EINLAGERN bestätigen
    if (target.id === 'lh-confirm-unload' && unloadModal) {
      const targetSel = document.getElementById('lh-unload-target') as HTMLSelectElement | null;
      if (targetSel) unloadModal.toPropertyId = targetSel.value;
      void handleUnload();
      return;
    }

    // Job-Fahrzeug setzen
    const setJobBtn = target.closest<HTMLButtonElement>('[data-lh-set-job-vehicle]');
    if (setJobBtn) {
      const vehicleId = setJobBtn.dataset.lhSetJobVehicle ?? '';
      void handleSetJobVehicle(vehicleId);
      return;
    }

    // Job-Fahrzeug freigeben
    const clearJobBtn = target.closest<HTMLButtonElement>('[data-lh-clear-job-vehicle]');
    if (clearJobBtn) {
      void handleClearJobVehicle();
      return;
    }

    // Fahrzeug verkaufen: erst Inline-Bestätigung, dann Verkauf
    const sellVehicleBtn = target.closest<HTMLButtonElement>('[data-lh-sell-vehicle]');
    if (sellVehicleBtn) {
      vehicleSellConfirmId = sellVehicleBtn.dataset.lhSellVehicle ?? null;
      rerender();
      return;
    }
    if (target.closest('[data-lh-sell-vehicle-cancel]')) {
      vehicleSellConfirmId = null;
      rerender();
      return;
    }
    const sellVehicleConfirmBtn = target.closest<HTMLButtonElement>('[data-lh-sell-vehicle-confirm]');
    if (sellVehicleConfirmBtn && !sellVehicleConfirmBtn.disabled) {
      sellVehicleConfirmBtn.disabled = true;
      void handleSellVehicle(sellVehicleConfirmBtn.dataset.lhSellVehicleConfirm ?? '');
      return;
    }
  });

  // VERSENDEN-Modal: direkte Mengeneingabe (State synchron halten + Confirm-Button-Status live aktualisieren)
  root.addEventListener('input', (e) => {
    const el = e.target as HTMLInputElement;
    if (!el.dataset.lhQtyInput || !sendModal) return;

    // Getippten Wert sofort in den State übernehmen — sonst rechnen +/- und MAX
    // mit dem alten item.qty weiter (State wurde bisher erst bei VERSENDEN-Klick synchronisiert).
    const key  = el.dataset.lhQtyInput;
    const item = sendModal.items.find((i) => i.itemKey === key);
    if (item) {
      const raw = parseInt(el.value, 10);
      if (!isNaN(raw)) item.qty = Math.max(1, Math.min(item.available, raw));
    }

    updateSendConfirmState();
    scheduleSendDurationPreview();
  });

  // Selektion (Transfer-Modal)
  root.addEventListener('change', (e) => {
    const target = e.target as HTMLElement;
    if ((target as HTMLSelectElement).id === 'lh-vehicle-select' && transferModal) transferModal.vehicleKey   = (target as HTMLSelectElement).value;
    if ((target as HTMLSelectElement).id === 'lh-target-select'  && transferModal) transferModal.toPropertyId = (target as HTMLSelectElement).value;

    // VERSENDEN-Modal: Fahrzeug
    if ((target as HTMLSelectElement).id === 'lh-send-vehicle' && sendModal) {
      sendModal.vehicleKey = (target as HTMLSelectElement).value;
      updateSendConfirmState();
      scheduleSendDurationPreview();
      return;
    }

    // VERSENDEN-Modal: Zielort
    if ((target as HTMLSelectElement).id === 'lh-send-target' && sendModal) {
      sendModal.toPropertyId = (target as HTMLSelectElement).value;
      // NT-VIERTEL-02: die Distanz (und damit die Fahrzeit) hängt jetzt vom Zielort ab — vorher
      // hatte ein Zielortwechsel keinen Effekt auf die Dauer, daher wurde hier nie neu geladen.
      updateSendConfirmState();
      scheduleSendDurationPreview();
      return;
    }

    // VERSENDEN-Modal: Checkbox
    const checkEl = target.closest<HTMLInputElement>('[data-lh-item-check]');
    if (checkEl && sendModal) {
      const key  = checkEl.dataset.lhItemCheck ?? '';
      const item = sendModal.items.find((i) => i.itemKey === key);
      if (item) {
        item.selected = checkEl.checked;
        // Qty-Buttons + Input aktivieren/deaktivieren
        document.querySelectorAll<HTMLButtonElement>(`[data-lh-item-inc="${key}"], [data-lh-item-dec="${key}"], [data-lh-item-max="${key}"]`).forEach((btn) => { btn.disabled = !item.selected; });
        const qtyInput = document.querySelector<HTMLInputElement>(`[data-lh-qty-input="${key}"]`);
        if (qtyInput) qtyInput.disabled = !item.selected;
        const qtyCtrl = qtyInput?.parentElement;
        if (qtyCtrl) qtyCtrl.classList.toggle('lh-send-qty-ctrl--disabled', !item.selected);
        // Confirm-Button Status aktualisieren
        updateSendConfirmState();
        scheduleSendDurationPreview();
      }
      return;
    }

    // EINLAGERN-Modal: Checkbox
    const unloadCheckEl = target.closest<HTMLInputElement>('[data-lh-unload-check]');
    if (unloadCheckEl && unloadModal) {
      const key     = unloadCheckEl.dataset.lhUnloadCheck ?? '';
      const checked = unloadCheckEl.checked;
      const group   = unloadModal.items.find((g) => g.itemKey === key);
      if (group) {
        group.selected = checked;
        if (checked && group.transferQty === 0) group.transferQty = group.totalQty;
        // Qty-Controls aktivieren/deaktivieren ohne Full-Rerender
        const ctrl = document.querySelector<HTMLElement>(`.lh-unload-qty-ctrl:has([data-lh-unload-dec="${key}"])`);
        ctrl?.classList.toggle('lh-unload-qty-ctrl--disabled', !checked);
        ctrl?.querySelectorAll('button').forEach((btn) => { btn.disabled = !checked; });
        const confirmBtn = document.getElementById('lh-confirm-unload') as HTMLButtonElement | null;
        if (confirmBtn) confirmBtn.disabled = !unloadModal.items.some((g) => g.selected && g.transferQty > 0);
      }
      return;
    }

    // EINLAGERN-Modal: Qty-Buttons
    const unloadDecEl = target.closest<HTMLButtonElement>('[data-lh-unload-dec]');
    if (unloadDecEl && unloadModal) {
      const key   = unloadDecEl.dataset.lhUnloadDec ?? '';
      const group = unloadModal.items.find((g) => g.itemKey === key);
      if (group) {
        const step = getPackUnitsPerPack(key) ?? 1;
        group.transferQty = Math.max(step, group.transferQty - step);
        updateUnloadQtyDisplay(key);
      }
      return;
    }
    const unloadIncEl = target.closest<HTMLButtonElement>('[data-lh-unload-inc]');
    if (unloadIncEl && unloadModal) {
      const key   = unloadIncEl.dataset.lhUnloadInc ?? '';
      const group = unloadModal.items.find((g) => g.itemKey === key);
      if (group) {
        const step = getPackUnitsPerPack(key) ?? 1;
        group.transferQty = Math.min(group.totalQty, group.transferQty + step);
        updateUnloadQtyDisplay(key);
      }
      return;
    }
    const unloadMaxEl = target.closest<HTMLButtonElement>('[data-lh-unload-max]');
    if (unloadMaxEl && unloadModal) {
      const key   = unloadMaxEl.dataset.lhUnloadMax ?? '';
      const group = unloadModal.items.find((g) => g.itemKey === key);
      if (group) { group.transferQty = group.totalQty; updateUnloadQtyDisplay(key); }
      return;
    }

    // EINLAGERN-Modal: Zielort
    if ((target as HTMLSelectElement).id === 'lh-unload-target' && unloadModal) {
      unloadModal.toPropertyId = (target as HTMLSelectElement).value;
      return;
    }

    // VERKAUFEN-Modal: Item-Auswahl
    if ((target as HTMLSelectElement).id === 'lh-sell-item-select' && sellModal) {
      const key = (target as HTMLSelectElement).value;
      sellModal.selectedItemKey = key || null;
      sellModal.qty = 1;
      sellModal.errorMsg = null;
      rerender();
      return;
    }

    // KONSUMIERBARE-Modal: Ziel-Auswahl (Pflanze/Transport)
    if ((target as HTMLSelectElement).id === 'lh-cons-target-select' && consumableUseModal) {
      consumableUseModal.targetId = (target as HTMLSelectElement).value || null;
      consumableUseModal.errorMsg = null;
      rerender();
      return;
    }

  });
}

async function handleSetJobVehicle(vehicleId: string): Promise<void> {
  try {
    await api.post(`/vehicle/${vehicleId}/set-job-vehicle`, {});
    showToast('Job-Fahrzeug gesetzt.', 'info');
    await reloadVehicleInventories();
    rerender();
  } catch (err) {
    const msg = err instanceof ApiError ? err.message : 'Fehler beim Setzen des Job-Fahrzeugs.';
    showToast(msg, 'error');
  }
}

async function handleSellVehicle(vehicleId: string): Promise<void> {
  try {
    const result = await api.post<{ erloesCents: string }>(`/vehicle/${vehicleId}/sell`, {});
    showToast(`Fahrzeug für ${fmtNox(result.erloesCents)} verkauft.`, 'info');
    void refreshBalance();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Verkauf fehlgeschlagen.', 'error');
  }
  vehicleSellConfirmId = null;
  await reload();
}

async function handleClearJobVehicle(): Promise<void> {
  try {
    await api.delete('/vehicle/job-vehicle');
    showToast('Job-Fahrzeug freigegeben.', 'info');
    await reloadVehicleInventories();
    rerender();
  } catch (err) {
    const msg = err instanceof ApiError ? err.message : 'Fehler beim Freigeben des Job-Fahrzeugs.';
    showToast(msg, 'error');
  }
}

async function handleStartTransport(): Promise<void> {
  if (!transferModal) return;
  const btn = document.getElementById('lh-confirm-transfer') as HTMLButtonElement | null;
  if (btn) { btn.disabled = true; btn.textContent = '...'; }

  try {
    const tm = transferModal;
    await api.post<unknown>('/transport/start', {
      fromPropertyId: tm.fromPropertyId,
      toPropertyId:   tm.toPropertyId,
      vehicleKey:     tm.vehicleKey,
      itemKey:        tm.itemKey,
      itemType:       tm.itemType,
      quantity:       tm.qty,
    });
    transferModal = null;
    showToast('Transport gestartet!', 'info');
    await reload();
  } catch (err) {
    if (tryOfferEmergencyGarage(err, () => { void reload(); })) { rerender(); return; }
    showToast(err instanceof ApiError ? err.message : 'Transport fehlgeschlagen.', 'error');
    rerender();
  }
}

async function handleSkipTransport(transportId: string): Promise<void> {
  const btn = document.querySelector<HTMLButtonElement>(`[data-lh-transport-skip="${CSS.escape(transportId)}"]`);
  if (btn) { btn.disabled = true; btn.textContent = '...'; }

  try {
    await api.post<{ scheineSpent: number; scheineRemaining: number }>(`/transport/${encodeURIComponent(transportId)}/skip`, {});
    showToast('Transport sofort angekommen.', 'info');
    void refreshBalance();
    await reload();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Skip fehlgeschlagen.', 'error');
    if (btn) { btn.disabled = false; btn.textContent = `${String(TRANSPORT_SKIP_COST_SCHEINE_DISPLAY)} SCHEINE - SOFORT`; }
  }
}

async function handleStartSend(): Promise<void> {
  if (!sendModal) return;
  const sm = sendModal;
  const btn = document.getElementById('lh-confirm-send') as HTMLButtonElement | null;
  if (btn) { btn.disabled = true; btn.textContent = '...'; }

  if (sm.vehicleKey === '' || sm.toPropertyId === '') {
    sendModal.errorMsg = 'Bitte Fahrzeug und Zielort wählen.';
    rerender();
    return;
  }
  const toSend = sm.items.filter((i) => i.selected && i.qty > 0);
  if (toSend.length === 0) {
    sendModal.errorMsg = 'Mindestens ein Gegenstand mit Menge > 0 auswählen.';
    rerender();
    return;
  }

  try {
    await api.post<unknown>('/transport/start', {
      fromPropertyId: sm.fromPropertyId,
      toPropertyId:   sm.toPropertyId,
      vehicleKey:     sm.vehicleKey,
      items:          toSend.map((i) => ({
        itemKey:  i.itemKey,
        itemType: i.itemType,
        quantity: isPackItem(i.itemKey) && i.sheetsAvailable !== undefined
          ? packsToUnits(i.itemKey, i.qty, i.sheetsAvailable)
          : i.qty,
      })),
    });
    sendModal = null;
    showToast(toSend.length === 1 ? 'Transport gestartet!' : 'Mischladung gestartet!', 'info');
  } catch (err) {
    if (tryOfferEmergencyGarage(err, () => { void reload(); })) {
      // Angebots-Modal übernimmt die Fehlerdarstellung — kein zusätzlicher Inline-Text.
    } else if (sendModal) {
      sendModal.errorMsg = err instanceof ApiError ? err.message : 'Transport fehlgeschlagen.';
    }
  }

  await reload();
}

async function handleUnload(): Promise<void> {
  if (!unloadModal) return;
  const um = unloadModal;
  const btn = document.getElementById('lh-confirm-unload') as HTMLButtonElement | null;
  if (btn) { btn.disabled = true; btn.textContent = '...'; }

  const selectedGroups = um.items.filter((g) => g.selected);
  if (selectedGroups.length === 0) {
    unloadModal.errorMsg = 'Mindestens einen Gegenstand auswählen.';
    rerender();
    return;
  }

  // transferQty auf die zugrundeliegenden DB-Slots verteilen (von vorne füllen)
  const slotsToTransfer: { slotIndex: number; quantity: number }[] = [];
  for (const group of selectedGroups) {
    let remaining = group.transferQty;
    for (const slot of group.slots) {
      if (remaining <= 0) break;
      const take = Math.min(remaining, slot.quantity);
      if (take > 0) slotsToTransfer.push({ slotIndex: slot.slotIndex, quantity: take });
      remaining -= take;
    }
  }

  try {
    await api.post<unknown>(`/vehicle/${um.vehicleId}/inventory/unload-batch`, {
      targetStorageId: um.toPropertyId,
      slots: slotsToTransfer,
    });
    const total = selectedGroups.reduce((sum, g) => sum + g.transferQty, 0);
    unloadModal = null;
    showToast(`${String(total)} Gegenstand${total !== 1 ? 'ände' : ''} eingelagert.`, 'info');
  } catch (err) {
    if (tryOfferEmergencyGarage(err, () => { void reload(); })) {
      // Angebots-Modal übernimmt die Fehlerdarstellung — kein zusätzlicher Inline-Text.
    } else if (unloadModal) {
      unloadModal.errorMsg = err instanceof ApiError ? err.message : 'Einlagern fehlgeschlagen.';
    }
  }

  await reload();
}

async function openSellModal(propertyId: string, propertyName: string): Promise<void> {
  try {
    const result = await api.get<{ items: SellableItem[] }>(`/inventory/sell/items?propertyId=${encodeURIComponent(propertyId)}`);
    sellModal = {
      propertyId,
      propertyName,
      items:           result.items,
      selectedItemKey: result.items[0]?.itemKey ?? null,
      qty:             1,
      errorMsg:        null,
    };
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Laden fehlgeschlagen.', 'error');
    sellModal = null;
  }
  rerender();
}

function updateUnloadQtyDisplay(itemKey: string): void {
  if (!unloadModal) return;
  const group = unloadModal.items.find((g) => g.itemKey === itemKey);
  if (!group) return;
  const display = document.querySelector<HTMLElement>(`[data-lh-unload-qval="${itemKey}"]`);
  if (display) {
    display.textContent = isPackItem(itemKey)
      ? `${String(packCount(itemKey, group.transferQty))} PKT`
      : String(group.transferQty);
  }
  const confirmBtn = document.getElementById('lh-confirm-unload') as HTMLButtonElement | null;
  if (confirmBtn) confirmBtn.disabled = !unloadModal.items.some((g) => g.selected && g.transferQty > 0);
}

function updateSellModalQty(): void {
  if (!sellModal) return;
  const selectedKey = sellModal.selectedItemKey;
  const item = sellModal.items.find((i) => i.itemKey === selectedKey);
  if (!item) return;
  const pricePerUnit = BigInt(item.pricePerUnit);
  const total = pricePerUnit * BigInt(sellModal.qty);
  const qtyDisplay   = document.getElementById('lh-sell-qty-display');
  const totalDisplay = document.getElementById('lh-sell-total');
  if (qtyDisplay)   qtyDisplay.textContent   = String(sellModal.qty);
  if (totalDisplay) totalDisplay.textContent = fmtNox(total.toString());
}

async function handleSell(): Promise<void> {
  if (!sellModal?.selectedItemKey) return;
  const sm = sellModal;
  const btn = document.getElementById('lh-confirm-sell') as HTMLButtonElement | null;
  if (btn) { btn.disabled = true; btn.textContent = '...'; }

  try {
    interface SellResponse { totalEarned: string; newNoxBalance: string; quantity: number; }
    const result = await api.post<SellResponse>('/inventory/sell', {
      propertyId: sm.propertyId,
      itemKey:    sm.selectedItemKey,
      quantity:   sm.qty,
    });
    sellModal = null;
    const item = sm.items.find((i) => i.itemKey === sm.selectedItemKey);
    showToast(`${String(result.quantity)}× ${item?.label ?? sm.selectedItemKey} für ${fmtNox(result.totalEarned)} verkauft.`, 'info');
    void refreshBalance(); // NT-HOTFIX-01 (H6)
    await reload();
  } catch (err) {
    if (sellModal) {
      sellModal.errorMsg = err instanceof ApiError ? err.message : 'Verkauf fehlgeschlagen.';
    }
    rerender();
  }
}

// ─── KONSUMIERBARE: Aktion (NT-CONSUMABLE-01) ──────────────────────────────

async function handleUseConsumable(): Promise<void> {
  if (!consumableUseModal) return;
  const m = consumableUseModal;
  const def = consumables.find((c) => c.itemType === m.itemType);
  if (def?.requiresTarget && !m.targetId) return;

  const btn = document.getElementById('lh-confirm-cons-use') as HTMLButtonElement | null;
  if (btn) { btn.disabled = true; btn.textContent = '...'; }

  try {
    interface UseResult { effectDescription: string; }
    const result = await api.post<UseResult>(
      `/consumables/${encodeURIComponent(m.itemType)}/use`,
      m.targetId ? { targetId: m.targetId } : {},
    );
    consumableUseModal = null;
    showToast(result.effectDescription, 'info');
    void refreshBalance(); // NT-HOTFIX-01 (H6): manche Trickkiste-Items schreiben Geld gut
    await reload();
  } catch (err) {
    if (consumableUseModal) {
      consumableUseModal.errorMsg = err instanceof ApiError ? err.message : 'Benutzung fehlgeschlagen.';
    }
    rerender();
  }
}

// ─── AN HEHLER / WASCHEN: Aktionen ──────────────────────────────────────────

async function handleSubmitHehler(): Promise<void> {
  if (!hehlerModal) return;
  const hm = hehlerModal;
  const selected = hm.items.filter((i) => i.selected && i.qty > 0);
  if (selected.length === 0) {
    hehlerModal.errorMsg = 'Mindestens einen Gegenstand auswählen.';
    rerender();
    return;
  }

  const btn = document.getElementById('lh-confirm-hehler') as HTMLButtonElement | null;
  if (btn) { btn.disabled = true; btn.textContent = '...'; }

  try {
    await api.post<unknown>('/hehler/orders', {
      sourcePropertyId: hm.propertyId,
      items: selected.map((i) => ({ itemKey: i.itemKey, quantity: i.qty })),
    });
    hehlerModal = null;
    showToast('Ware an den Hehler übergeben. Auszahlung folgt nach der Bearbeitungszeit.', 'info');
    await reloadHehlerOrders();
    await reload();
  } catch (err) {
    if (hehlerModal) {
      hehlerModal.errorMsg = err instanceof ApiError ? err.message : 'Übergabe fehlgeschlagen.';
    }
    rerender();
  }
}

async function openWashModal(propertyId: string, available: number): Promise<void> {
  try {
    const result = await api.get<{ businesses: LaundryBusinessOption[] }>('/laundry/businesses');
    const usable = result.businesses.filter((b) => b.status !== 'FROZEN');
    washModal = {
      propertyId,
      available,
      qty:                1,
      businesses:         result.businesses,
      selectedBusinessId: usable[0]?.id ?? null,
      errorMsg:           null,
    };
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Laden fehlgeschlagen.', 'error');
    washModal = { propertyId, available, qty: 1, businesses: [], selectedBusinessId: null, errorMsg: null };
  }
  rerender();
}

async function handleWashBargeldBuendel(): Promise<void> {
  if (!washModal?.selectedBusinessId) return;
  const wm = washModal;
  const btn = document.getElementById('lh-confirm-wash') as HTMLButtonElement | null;
  if (btn) { btn.disabled = true; btn.textContent = '...'; }

  try {
    await api.post<unknown>('/hehler/bargeld-buendel/geldwaesche', {
      businessId:       wm.selectedBusinessId,
      sourcePropertyId: wm.propertyId,
      quantity:         wm.qty,
    });
    washModal = null;
    hehlerModal = null;
    showToast('Bargeld-Bündel zum Waschen übergeben. NOX folgt nach der Wäsche-Zeit.', 'info');
    await reloadHehlerOrders();
    await reload();
  } catch (err) {
    if (washModal) {
      washModal.errorMsg = err instanceof ApiError ? err.message : 'Übergabe fehlgeschlagen.';
    }
    rerender();
  }
}

// ─── Lager-Ausbau / Fahrzeugslot: Aktionen (NT-STORAGE-UPGRADE-01) ──────────────

async function handleStartStorageUpgrade(propertyId: string): Promise<void> {
  const btn = document.querySelector<HTMLButtonElement>(`[data-lh-upgrade-start="${CSS.escape(propertyId)}"]`);
  if (btn) { btn.disabled = true; btn.textContent = '...'; }
  try {
    await api.post<unknown>(`/properties/${encodeURIComponent(propertyId)}/storage-upgrade`, {});
    showToast('Ausbau gestartet.', 'info');
    void refreshBalance();
    await reload();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Ausbau fehlgeschlagen.', 'error');
    if (btn) btn.disabled = false;
  }
}

async function handleBuyVehicleSlot(propertyId: string): Promise<void> {
  const btn = document.querySelector<HTMLButtonElement>(`[data-lh-vslot-buy="${CSS.escape(propertyId)}"]`);
  if (btn) { btn.disabled = true; btn.textContent = '...'; }
  try {
    await api.post<unknown>(`/properties/${encodeURIComponent(propertyId)}/vehicle-slot/buy`, {});
    showToast('Fahrzeugslot gekauft.', 'info');
    void refreshBalance();
    await reload();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Kauf fehlgeschlagen.', 'error');
    if (btn) { btn.disabled = false; btn.textContent = `STELLPLATZ KAUFEN (${fmtNox(VEHICLE_SLOT_COST_CENTS_DISPLAY)})`; }
  }
}

async function handleAssignVehicleSlot(propertyId: string, vehicleId: string): Promise<void> {
  const btn = document.querySelector<HTMLButtonElement>(`[data-lh-vslot-assign="${CSS.escape(propertyId)}"]`);
  if (btn) { btn.disabled = true; btn.textContent = '...'; }
  try {
    await api.post<unknown>(`/properties/${encodeURIComponent(propertyId)}/vehicle-slot/assign`, { vehicleId });
    showToast('Fahrzeug zugewiesen.', 'info');
    await reload();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Zuweisung fehlgeschlagen.', 'error');
    if (btn) { btn.disabled = false; btn.textContent = 'ZUWEISEN'; }
  }
}

async function handleUnassignVehicleSlot(propertyId: string): Promise<void> {
  const btn = document.querySelector<HTMLButtonElement>(`[data-lh-vslot-unassign="${CSS.escape(propertyId)}"]`);
  if (btn) { btn.disabled = true; btn.textContent = '...'; }
  try {
    await api.post<unknown>(`/properties/${encodeURIComponent(propertyId)}/vehicle-slot/unassign`, {});
    showToast('Fahrzeug abgemeldet.', 'info');
    await reload();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Abmelden fehlgeschlagen.', 'error');
    if (btn) { btn.disabled = false; btn.textContent = 'ABMELDEN'; }
  }
}

async function handleSaveNickname(propertyId: string, value: string): Promise<void> {
  const trimmed = value.trim();
  try {
    await api.patch<{ nickname: string | null }>(
      `/properties/${encodeURIComponent(propertyId)}/nickname`,
      { nickname: trimmed === '' ? null : trimmed },
    );
    showToast('Spitzname gespeichert.', 'info');
    await reload();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Spitzname konnte nicht gespeichert werden.', 'error');
  }
}

// ─── Lager-Dedizierung: Aktionen (Pfand: NT-PFAND-LAGER-LOOPB-01, Werkstatt: NT-WERKSTATT-PUFFER-REAL-INVENTORY-01) ────────────────

async function handleDedicateWarehouse(propertyId: string): Promise<void> {
  const btn = document.querySelector<HTMLButtonElement>(`[data-lh-dedicate="${CSS.escape(propertyId)}"]`);
  if (btn) { btn.disabled = true; btn.textContent = '...'; }
  try {
    await api.post<unknown>(`/properties/${encodeURIComponent(propertyId)}/dedicate-pfand-lager`, {});
    showToast('Lager für Pfand-Lager dediziert.', 'info');
    await reload();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Dedizieren fehlgeschlagen.', 'error');
    if (btn) { btn.disabled = false; btn.textContent = 'ALS PFAND-LAGER DEDIZIEREN'; }
  }
}

async function handleDedicateWerkstattWarehouse(propertyId: string): Promise<void> {
  const btn = document.querySelector<HTMLButtonElement>(`[data-lh-dedicate-werkstatt="${CSS.escape(propertyId)}"]`);
  if (btn) { btn.disabled = true; btn.textContent = '...'; }
  try {
    await api.post<unknown>(`/properties/${encodeURIComponent(propertyId)}/dedicate-werkstatt-lager`, {});
    showToast('Lager für die Werkstatt dediziert.', 'info');
    await reload();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Dedizieren fehlgeschlagen.', 'error');
    if (btn) { btn.disabled = false; btn.textContent = 'ALS WERKSTATT-LAGER DEDIZIEREN'; }
  }
}

async function handleUndedicateWarehouse(propertyId: string): Promise<void> {
  const btn = document.querySelector<HTMLButtonElement>(`[data-lh-undedicate="${CSS.escape(propertyId)}"]`);
  if (btn) { btn.disabled = true; btn.textContent = '...'; }
  try {
    await api.post<unknown>(`/properties/${encodeURIComponent(propertyId)}/undedicate`, {});
    showToast('Dedizierung aufgehoben.', 'info');
    await reload();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Aufheben fehlgeschlagen.', 'error');
    if (btn) { btn.disabled = false; btn.textContent = 'AUFHEBEN'; }
  }
}

async function handleSkipHehler(orderId: string): Promise<void> {
  const btn = document.querySelector<HTMLButtonElement>(`[data-lh-hehler-skip="${CSS.escape(orderId)}"]`);
  if (btn) { btn.disabled = true; btn.textContent = '...'; }

  try {
    await api.post<{ scheineSpent: number; scheineRemaining: number }>(`/hehler/${encodeURIComponent(orderId)}/skip`, {});
    showToast('Hehler-Auftrag sofort ausgezahlt.', 'info');
    void refreshBalance();
    await reloadHehlerOrders();
    rerender();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Skip fehlgeschlagen.', 'error');
    if (btn) { btn.disabled = false; btn.textContent = `${String(HEHLER_SKIP_COST_SCHEINE_DISPLAY)} SCHEINE - SOFORT`; }
  }
}

// ─── Countdown in-place ───────────────────────────────────────────────────────

function updateCountdowns(): void {
  const now = Date.now();
  for (const o of pendingOrders) {
    const el = document.querySelector<HTMLElement>(`[data-lh-order="${o.id}"]`);
    if (el) el.textContent = fmtCountdown(Math.max(0, new Date(o.deliveredAt).getTime() - now));
  }
  for (const t of transportData?.active ?? []) {
    document.querySelectorAll<HTMLElement>(`[data-lh-transport="${t.id}"]`).forEach((el) => {
      el.textContent = fmtCountdown(Math.max(0, new Date(t.arrivesAt).getTime() - now));
    });
  }
  for (const o of hehlerOrders) {
    if (o.status !== 'IN_PROGRESS') continue;
    const el = document.querySelector<HTMLElement>(`[data-lh-hehler-order="${o.id}"]`);
    if (el) el.textContent = fmtCountdown(Math.max(0, new Date(o.payoutAt).getTime() - now));
  }
  for (const p of propsData?.warehouses ?? []) {
    if (!p.storageUpgradeCompletesAt) continue;
    const el = document.querySelector<HTMLElement>(`[data-lh-upgrade="${p.id}"]`);
    if (el) el.textContent = fmtCountdown(Math.max(0, new Date(p.storageUpgradeCompletesAt).getTime() - now));
  }
}

// ─── Daten laden ──────────────────────────────────────────────────────────────

async function reloadVehicleInventories(): Promise<void> {
  try {
    const listResult = await api.get<{ vehicles: VehicleListItem[] }>('/vehicle/list');
    vehicleList = listResult.vehicles;
    const invResults = await Promise.allSettled(
      listResult.vehicles
        .filter((v) => v.capacity > 0)
        .map((v) => api.get<VehicleInventoryData>(`/vehicle/${v.id}/inventory`)),
    );
    vehicleInventories = invResults
      .filter((r): r is PromiseFulfilledResult<VehicleInventoryData> => r.status === 'fulfilled')
      .map((r) => r.value);
  } catch (err) {
    console.error('[Lagerhaus] /vehicles Fehler:', err);
    vehicleInventories = [];
    vehicleList = [];
  }
}

async function reloadHehlerOrders(): Promise<void> {
  const previouslyOpen = new Set(hehlerOrders.filter((o) => o.status === 'IN_PROGRESS').map((o) => o.id));
  try {
    hehlerOrders = await api.get<HehlerOrderInfo[]>('/hehler/orders');
  } catch (err) {
    console.error('[Lagerhaus] /hehler/orders Fehler:', err);
    hehlerOrders = [];
    return;
  }
  // NT-HOTFIX-01 (H6): GET /hehler/orders zahlt fällige Aufträge lazy aus — ist ein vorher offener
  // Auftrag jetzt abgeschlossen, hat sich der Kontostand geändert.
  const paidOut = hehlerOrders.some((o) => previouslyOpen.has(o.id) && o.status !== 'IN_PROGRESS');
  if (paidOut) void refreshBalance();
}

interface HideoutSlotForPlants { activePlants: { id: string; strainKey: string; phase: string; isHarvested: boolean }[]; }
interface HideoutForPlants { propertySlots: HideoutSlotForPlants[]; }

/**
 * Lädt Bestand + Tages-Nutzung der Konsumierbaren (NT-CONSUMABLE-01) sowie die GROWING-Pflanzen
 * des Spielers (Ziel-Auswahl für DUENGER_BEUTEL/TURBO_DUENGER) — letzteres nur die schlanken
 * Felder, die für das Ziel-Dropdown gebraucht werden, kein vollständiger Gewächshaus-Fetch.
 * Zusätzlich (NT-CONSUMABLE-02): Jobs im Cooldown als Ziel-Auswahl für PARKPLATZ_ZETTEL — nutzt
 * das bereits bestehende, schlanke `cooldowns`-Feld aus /jobs/status statt eines Sonder-Endpoints.
 */
async function reloadConsumables(): Promise<void> {
  const [consumablesResult, hideoutsResult, jobsResult] = await Promise.allSettled([
    api.get<{ items: ConsumableInventoryEntry[] }>('/consumables'),
    api.get<{ hideouts: HideoutForPlants[] }>('/hideouts'),
    api.get<{ cooldowns: { jobId: string; jobName: string; cooldownEndsAt: string | null }[] }>('/jobs/status'),
  ]);

  if (consumablesResult.status === 'fulfilled') { consumables = consumablesResult.value.items; }
  else { console.error('[Lagerhaus] /consumables Fehler:', consumablesResult.reason); consumables = []; }

  if (hideoutsResult.status === 'fulfilled') {
    growingPlants = hideoutsResult.value.hideouts
      .flatMap((h) => h.propertySlots)
      .flatMap((s) => s.activePlants)
      .filter((p) => !p.isHarvested && p.phase === 'GROWING')
      .map((p) => ({ id: p.id, strainKey: p.strainKey }));
  } else {
    console.error('[Lagerhaus] /hideouts Fehler:', hideoutsResult.reason);
    growingPlants = [];
  }

  if (jobsResult.status === 'fulfilled') {
    const now = Date.now();
    jobsInCooldown = jobsResult.value.cooldowns
      .filter((c) => c.cooldownEndsAt !== null && new Date(c.cooldownEndsAt).getTime() > now)
      .map((c) => ({ jobId: c.jobId, jobName: c.jobName }));
  } else {
    console.error('[Lagerhaus] /jobs/status Fehler:', jobsResult.reason);
    jobsInCooldown = [];
  }
}

/** @param background true = Aufruf aus dem Polling-Timer (Render-Pause bei offener Auswahl, H7). */
async function reload(background = false): Promise<void> {
  const [propsResult, ordersResult, transportResult] = await Promise.allSettled([
    api.get<PropertiesResponse>('/properties'),
    api.get<{ orders: PendingOrder[] }>('/market/orders'),
    api.get<TransportStatusResponse>('/transport/status'),
  ]);

  if (propsResult.status === 'fulfilled')     { propsData     = propsResult.value; }
  else { console.error('[Lagerhaus] /properties Fehler:', propsResult.reason); }

  if (ordersResult.status === 'fulfilled')    { pendingOrders = ordersResult.value.orders; }
  else { console.error('[Lagerhaus] /market/orders Fehler:', ordersResult.reason); }

  if (transportResult.status === 'fulfilled') { transportData = transportResult.value; }
  else { console.error('[Lagerhaus] /transport/status Fehler:', transportResult.reason); }

  await reloadVehicleInventories();
  await reloadHehlerOrders();
  await reloadConsumables();

  // NT-HOTFIX-01 (H7): Polling-Refresh nicht über ein offenes Dropdown/Modal hinweg rendern.
  if (background) renderWhenIdle(LAGERHAUS_RENDER_KEY, 'lh-root', rerender);
  else rerender();
}

// showToast aus utils/toast.ts importiert

// ─── Lifecycle ───────────────────────────────────────────────────────────────

export function renderLagerhausTab(): string {
  return `<section class="dash-content" style="padding:0;" id="lh-root"><p style="font-family:var(--font-mono);font-size:12px;color:var(--text-muted);text-align:center;padding:var(--space-8) 0;letter-spacing:.08em;">LADE...</p></section>`;
}

export function mountLagerhausTab(): void {
  addStyles();
  void reload();
  refreshTimer   = setInterval(() => { void reload(true); }, 60_000);
  countdownTimer = setInterval(updateCountdowns, 1_000);
}

export function cleanupLagerhaus(): void {
  if (refreshTimer   !== null) { clearInterval(refreshTimer);   refreshTimer   = null; }
  if (countdownTimer !== null) { clearInterval(countdownTimer); countdownTimer = null; }
  propsData          = null;
  pendingOrders      = [];
  transportData      = null;
  vehicleInventories = [];
  transferModal      = null;
  sendModal          = null;
  unloadModal        = null;
  sellModal          = null;
  hehlerModal        = null;
  washModal          = null;
  hehlerOrders       = [];
  consumables        = [];
  growingPlants      = [];
  consumableUseModal = null;
  expandedCards.clear();
  expandedTransportDetails.clear();
  activeTab = 'lager';
  expandedVehicleCards.clear();
  cancelPendingRender(LAGERHAUS_RENDER_KEY);
}
